/**
 * Thin TanStack-Query wrappers around the API. Each hook returns a
 * typed `useQuery` result; callers get `{data, isLoading, error}` and
 * don't have to know about axios or cache keys.
 *
 * Query key convention: `[resource, …params]`. The query key fully
 * determines the response shape, so invalidating by prefix (e.g.
 * `["overview"]`) nukes all filter variants at once — used from the
 * Topbar refresh button.
 */

import * as React from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { api, ensureFreshAccessToken, getAccessToken } from "./api";
import { pickerApps } from "./app-visibility";
import { pollMs as adaptyPollMs } from "./adapty-webhook-apps";


// ----------------------------------------------------------------------
// Optimistic-update helpers for ASA write mutations.
// ----------------------------------------------------------------------
//
// PATCH /api/{campaigns,ad-groups,keywords}/:id returns 202 instantly
// (op queued in pending_writes) — the actual ASA push and mirror-table
// update happen in the worker drainer ~3-5s later. If we invalidate
// queries on PATCH-success, the immediate refetch sees the OLD value
// in asa_*_meta and the user's edit appears to revert. Fix: patch the
// cached rows in-place before sending, then schedule a couple of
// re-invalidations on a delay so the UI converges to the actual
// ASA-confirmed value once the worker has caught up. If ASA rejects
// the change, the mirror keeps the old value and the delayed refetch
// reverts the optimistic patch automatically.

/** Walk every cached query under the given prefix and patch any row
 *  matching `predicate`. Returns the saved data references for
 *  rollback in `onError`. */
function patchCachedRows<T extends Record<string, unknown>>(
  qc: QueryClient,
  prefix: readonly unknown[],
  predicate: (row: T) => boolean,
  patch: Partial<T>,
): Array<[QueryKey, unknown]> {
  const snapshot: Array<[QueryKey, unknown]> = [];
  const entries = qc.getQueriesData({ queryKey: prefix });
  for (const [key, data] of entries) {
    if (data == null) continue;
    snapshot.push([key, data]);
    if (Array.isArray(data)) {
      const next = (data as T[]).map((r) =>
        predicate(r) ? ({ ...r, ...patch } as T) : r
      );
      qc.setQueryData(key, next);
    } else if (typeof data === "object" && Array.isArray((data as { rows?: unknown[] }).rows)) {
      const obj = data as { rows: T[] };
      const nextRows = obj.rows.map((r) =>
        predicate(r) ? ({ ...r, ...patch } as T) : r
      );
      qc.setQueryData(key, { ...obj, rows: nextRows });
    }
  }
  return snapshot;
}

function restoreCachedRows(
  qc: QueryClient,
  snapshot: Array<[QueryKey, unknown]>,
): void {
  for (const [key, data] of snapshot) {
    qc.setQueryData(key, data);
  }
}

/** Schedule N invalidations on a delay so the UI converges to the
 *  worker-confirmed value once asa_*_meta updates.
 *
 *  Primary path now: the pending-writes tracker (lib/pending-writes-
 *  tracker.ts) polls every 1.5s and invalidates the moment ASA
 *  confirms. This `scheduleConfirmationRefetch` is the SAFETY NET for
 *  cases the tracker doesn't cover — mutation errored before producing
 *  a pending_write_id, drainer is stuck and timed out, etc. Tightened
 *  from [4000, 12000] to [1500, 5000, 12000] so even the fallback
 *  feels closer to live than the original 4s blind wait. */
function scheduleConfirmationRefetch(
  qc: QueryClient,
  prefixes: ReadonlyArray<readonly unknown[]>,
  delaysMs: ReadonlyArray<number> = [1500, 5000, 12000],
): void {
  for (const delay of delaysMs) {
    window.setTimeout(() => {
      for (const prefix of prefixes) {
        void qc.invalidateQueries({ queryKey: prefix });
      }
    }, delay);
  }
}
import type {
  AdaptyWebhookApp,
  AdaptyWebhookAppSave,
  BillingOverview,
  EntityChangesResponse,
  EntityChartResponse,
  AdGroupMirrorRow,
  AdGroupsStatsResponse,
  AdRow,
  App,
  AsaBillingRequest,
  AsaBillingResponse,
  AsaIntegrationResponse,
  AsaAccessLevel,
  AppleOAuthAccount,
  AuditLogResponse,
  BudgetOrderOption,
  BulkApplyCppResponse,
  BulkPauseCppResponse,
  BulkPauseCppTarget,
  ByAppResponse,
  ByCountryResponse,
  CampaignsStatsResponse,
  ColumnPreset,
  ConnectionTestResponse,
  CsCountry,
  CsCsvUploadResponse,
  CsCampaignDevicesResponse,
  CsDeviceChoice,
  CsFlow1Request,
  CsFlow2Request,
  CsFlow3PreviewResponse,
  CsFlow3Request,
  CsJobCreateResponse,
  CsJobProgress,
  CsJobReport,
  CsNameCheckResponse,
  CustomProductPagesResponse,
  DashboardView,
  EventMappingResponse,
  IntegrationSummary,
  KeywordsStatsResponse,
  Label,
  NameTemplate,
  NameTemplateCreateRequest,
  NameTemplateUpdateRequest,
  NegativeKeywordRow,
  OverviewEventSeriesResponse,
  OverviewResponse,
  OverviewTilesConfig,
  OverviewTilesUpsertRequest,
  OverviewViewConfig,
  AppAccessItem,
  AssignableRole,
  PendingWriteHandle,
  ProviderDescriptor,
  SearchTermsResponse,
  StatsFilters,
  TranslationFields,
  UserCreateResponse,
  UserRow,
} from "./types";
import { trackPendingWrite } from "./pending-writes-tracker";
import { trackCampaignStudioJob } from "./campaign-studio-tracker";

// --- /api/apps ------------------------------------------------------------

async function fetchApps(): Promise<App[]> {
  const { data } = await api.get<App[]>("/api/apps");
  return data;
}

/** The app catalogue as every picker shows it: no Android rows (they exist
 *  only to receive Adapty events; see `pickerApps`). */
export function useApps() {
  return useQuery({ queryKey: ["apps"], queryFn: fetchApps, select: pickerApps });
}

/** The whole catalogue, Android rows included — only for the Adapty per-app
 *  webhook URL card, which links them. */
export function useCatalogueApps() {
  return useQuery({ queryKey: ["apps"], queryFn: fetchApps });
}

// --- /api/stats/overview --------------------------------------------------

/** Builds the URLSearchParams for a stats request. Arrays become repeated
 *  params (app_ids=1&app_ids=2) which is what FastAPI's list[int] Query
 *  expects. */
function filtersToParams(f: StatsFilters): URLSearchParams {
  const p = new URLSearchParams();
  p.set("date_from", f.date_from);
  p.set("date_to", f.date_to);
  if (f.compare_previous) p.set("compare_previous", "true");
  (f.app_ids ?? []).forEach((v) => p.append("app_ids", String(v)));
  (f.campaign_ids ?? []).forEach((v) => p.append("campaign_ids", String(v)));
  (f.ad_group_ids ?? []).forEach((v) => p.append("ad_group_ids", String(v)));
  (f.countries ?? []).forEach((v) => p.append("countries", v));
  return p;
}

export function useOverview(filters: StatsFilters) {
  return useQuery({
    queryKey: ["overview", filters],
    queryFn: async () => {
      const { data } = await api.get<OverviewResponse>("/api/stats/overview", {
        params: filtersToParams(filters),
      });
      return data;
    },
    // Spend updates every ~5 min on the backend; refetching more often
    // wouldn't show new data. Refetch on tab focus so returning to the
    // app always shows fresh numbers. While an ASA initial sync is
    // running we poll every 15s so the banner disappears promptly
    // once today + T-1..T-3 have landed — no manual refresh required.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data?.sync_status.initial_sync_in_progress ? 15_000 : false,
  });
}

export function useByApp(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats-by-app", filters],
    queryFn: async () => {
      const { data } = await api.get<ByAppResponse>("/api/stats/by-app", {
        params: filtersToParams(filters),
      });
      return data;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Force-resync the apps list from ASA. Hits `GET /v5/campaigns` server-
 *  side, extracts distinct adamIds, and upserts any new ones into the
 *  local `apps` table (name + icon come from iTunes Lookup).
 *
 *  Use case: user added a new app in their ASA dashboard, wants it to
 *  show up in the OneAds picker without waiting for the next 5-min
 *  collector tick. This endpoint fires on demand.
 *
 *  Catches: any app with ≥ 1 campaign in ASA. Does NOT catch apps
 *  with zero campaigns — those need manual add via `useCreateApp`.
 */
/** Re-read one app's iPad support from Apple Ads (the refresh icon in
 *  the Campaign Studio app picker). */
export function useRefreshDeviceSupport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (appId: number) => {
      const { data } = await api.post<{
        app_id: number;
        ipad_supported: boolean;
        ipad_checked_at: string | null;
      }>(`/api/apps/${appId}/device-support/refresh`);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

export function useRefreshAppsFromAsa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        discovered: number;
        refreshed: number;
        seen_in_asa: number;
        total_local: number;
        sources: { account_apps: number; campaigns: number };
      }>("/api/apps/refresh-from-asa");
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

/** Resolved default App for the Ads Manager CampaignsPage.
 *
 *  Chain (per user 2026-05-16, server-side in
 *  app.services.default_app_service):
 *    1. User's sticky pick for this org's CampaignsPage (remembered
 *       across visits). Validated app still exists.
 *    2. Org-level cache populated by nightly worker job: top app by
 *       30-FULL-day spend, tie-break lifetime spend → name → id.
 *    3. Fallback to top app by 12-month spend (same tie-break).
 *    4. No spend anywhere → kind='all_apps' (frontend selects "all").
 *    5. Org has no apps → kind='no_apps' (frontend hides picker).
 *
 *  `kind` is informational — frontend acts on (`app_id`, `kind`):
 *  app_id != null ⇒ select that app; app_id == null && kind='all_apps'
 *  ⇒ "all"; app_id == null && kind='no_apps' ⇒ hide picker.
 */
export type DefaultAppKind =
  | "user_sticky"
  | "recent_spender"
  | "historical_spender"
  | "all_apps"
  | "no_apps";

export type DefaultAppResponse = {
  app_id: number | null;
  kind: DefaultAppKind;
};

export function useDefaultApp(opts: { enabled: boolean; orgId: number | null | undefined }) {
  return useQuery({
    // Key on orgId. The JWT carries active_org_id, and the backend
    // route uses it — but TanStack Query has no way to know the JWT
    // changed without us telling it. Without orgId in the key, the
    // post-org-switch render returns the PREVIOUS org's cached
    // default until staleTime expires.
    queryKey: ["ads-manager-default-app", opts.orgId],
    queryFn: async (): Promise<DefaultAppResponse> => {
      const { data } = await api.get<DefaultAppResponse>(
        "/api/ads-manager/default-app",
      );
      return data;
    },
    enabled: opts.enabled && opts.orgId != null,
    // 5min stale — sticky pick changes are pushed by mutation so
    // staleness only matters across page reloads while the user is
    // away. Nightly job moves the org-cache value, but the user's
    // sticky pick dominates anyway.
    staleTime: 5 * 60_000,
  });
}

/** Persist the user's explicit pick on the CampaignsPage app filter.
 *  Pass `app_id` for a concrete app or `is_all: true` for "all". */
export function useSetDefaultApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number | null; is_all: boolean }) => {
      const { data } = await api.put<DefaultAppResponse>(
        "/api/ads-manager/default-app",
        args,
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate all org-scoped variants — the resolver re-runs on
      // the next render with fresh server data. We don't preemptively
      // setQueryData because we'd need to know the active orgId here
      // (mutation context doesn't carry it); refetch is cheap.
      void qc.invalidateQueries({ queryKey: ["ads-manager-default-app"] });
    },
  });
}

/** Per-country rollup for the "Performance by country" card on Overview.
 *  Only fetched when the dashboard is scoped to a single app — `enabled`
 *  gates the network call so we don't request it when the widget isn't
 *  visible. */
export function useByCountry(filters: StatsFilters, enabled: boolean) {
  return useQuery({
    queryKey: ["stats-by-country", filters],
    queryFn: async () => {
      const { data } = await api.get<ByCountryResponse>(
        "/api/stats/by-country",
        { params: filtersToParams(filters) }
      );
      return data;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled,
  });
}

// --- /api/stats/{campaigns,ad-groups,keywords} — Ads Manager table -------

type PageParams = { limit?: number; offset?: number };
type CampaignStatus = "all" | "active" | "paused";

/** User-controllable sort. `by` is a ColumnKey (frontend) which the
 *  backend maps to a SQL ORDER BY expression — see
 *  app.services.stats_service._build_outer_order_by for the supported
 *  set. Unknown keys silently fall back to spend desc. */
export type SortParams = {
  by: string | null;
  dir: "asc" | "desc";
};

export function useCampaignsTable(
  filters: StatsFilters,
  page: PageParams = {},
  status: CampaignStatus = "all",
  sort: SortParams = { by: null, dir: "desc" },
  search?: string,
  filter?: string,
  /** «Split by devices» — each row gets `devices` sub-rows. */
  split?: boolean
) {
  const searchTrimmed = (search ?? "").trim();
  return useQuery({
    queryKey: ["stats-campaigns", filters, page, status, sort, searchTrimmed, filter ?? "", !!split],
    // A sort / page / search change makes a new query key; without this the
    // rows vanish, the table unmounts and the horizontal scroll snaps back
    // to the first column. Keep the previous rows until the new ones land.
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const params = filtersToParams(filters);
      if (page.limit !== undefined) params.set("limit", String(page.limit));
      if (page.offset !== undefined) params.set("offset", String(page.offset));
      if (status !== "all") params.set("status", status);
      if (sort.by) {
        params.set("sort_by", sort.by);
        params.set("sort_dir", sort.dir);
      }
      if (searchTrimmed) params.set("search", searchTrimmed);
      if (filter) params.set("filter", filter);
      if (split) params.set("split", "devices");
      const { data } = await api.get<CampaignsStatsResponse>("/api/stats/campaigns", {
        params,
      });
      return data;
    },
    staleTime: 60_000,
  });
}

export function useAdGroupsTable(
  filters: StatsFilters,
  campaignId: number | null,
  page: PageParams = {},
  sort: SortParams = { by: null, dir: "desc" },
  search?: string,
  status: "all" | "active" | "paused" = "all",
  filter?: string,
  /** «Split by devices» — each row gets `devices` sub-rows. */
  split?: boolean
) {
  const searchTrimmed = (search ?? "").trim();
  return useQuery({
    queryKey: ["stats-ad-groups", filters, campaignId, page, sort, searchTrimmed, status, filter ?? "", !!split],
    // A sort / page / search change makes a new query key; without this the
    // rows vanish, the table unmounts and the horizontal scroll snaps back
    // to the first column. Keep the previous rows until the new ones land.
    placeholderData: (prev) => prev,
    // Only fire when a campaign is being drilled into — no sense pulling
    // every ad group across the org every time the parent list mounts.
    enabled: campaignId !== null,
    queryFn: async () => {
      const params = filtersToParams({
        ...filters,
        campaign_ids: campaignId !== null ? [campaignId] : null,
      });
      if (page.limit !== undefined) params.set("limit", String(page.limit));
      if (page.offset !== undefined) params.set("offset", String(page.offset));
      if (sort.by) {
        params.set("sort_by", sort.by);
        params.set("sort_dir", sort.dir);
      }
      if (searchTrimmed) params.set("search", searchTrimmed);
      if (filter) params.set("filter", filter);
      if (split) params.set("split", "devices");
      if (status !== "all") params.set("status", status);
      const { data } = await api.get<AdGroupsStatsResponse>("/api/stats/ad-groups", {
        params,
      });
      return data;
    },
    staleTime: 60_000,
  });
}

export function useKeywordsTable(
  filters: StatsFilters,
  adGroupId: number | null,
  page: PageParams = {},
  sort: SortParams = { by: null, dir: "desc" },
  search?: string,
  status: "all" | "active" | "paused" = "all",
  filter?: string,
  /** «Split by devices» — each row gets `devices` sub-rows. */
  split?: boolean
) {
  // Scope resolution: when the caller already supplied `campaign_ids`
  // or `ad_group_ids` in `filters`, respect them — that's the path the
  // campaign-page "All Keywords" tab uses to show every keyword across
  // every ad group in the campaign without forcing the user to pick
  // one first. Otherwise fall back to the single-adGroupId shortcut
  // (the ad-group drill-down path).
  const callerScoped =
    (filters.campaign_ids && filters.campaign_ids.length > 0) ||
    (filters.ad_group_ids && filters.ad_group_ids.length > 0);
  const searchTrimmed = (search ?? "").trim();
  return useQuery({
    queryKey: ["stats-keywords", filters, adGroupId, page, sort, searchTrimmed, status, filter ?? "", !!split],
    // A sort / page / search change makes a new query key; without this the
    // rows vanish, the table unmounts and the horizontal scroll snaps back
    // to the first column. Keep the previous rows until the new ones land.
    placeholderData: (prev) => prev,
    enabled: callerScoped || adGroupId !== null,
    queryFn: async () => {
      const effective: StatsFilters = callerScoped
        ? filters
        : { ...filters, ad_group_ids: adGroupId !== null ? [adGroupId] : null };
      const params = filtersToParams(effective);
      if (page.limit !== undefined) params.set("limit", String(page.limit));
      if (page.offset !== undefined) params.set("offset", String(page.offset));
      if (sort.by) {
        params.set("sort_by", sort.by);
        params.set("sort_dir", sort.dir);
      }
      if (searchTrimmed) params.set("search", searchTrimmed);
      if (status !== "all") params.set("status", status);
      if (filter) params.set("filter", filter);
      if (split) params.set("split", "devices");
      const { data } = await api.get<KeywordsStatsResponse>("/api/stats/keywords", {
        params,
      });
      return data;
    },
    staleTime: 60_000,
  });
}

// Light-weight count probes scoped to a list of campaign_ids — used by
// the Bulk Editor modal's sidebar to show "N campaigns / N ad groups /
// N keywords" pills. We hit the same /api/stats endpoints with limit=1
// just to read the `total` field; the cost is one server-side count
// query per probe (cheap, indexed). Falls back to 0 when no campaigns
// are selected so the modal doesn't render NaN.
export function useAdGroupsCountForCampaigns(
  campaignIds: number[],
  statsFilters: StatsFilters,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["stats-ad-groups-count", campaignIds, statsFilters],
    enabled: enabled && campaignIds.length > 0,
    queryFn: async () => {
      const params = filtersToParams({
        ...statsFilters,
        campaign_ids: campaignIds,
      });
      params.set("limit", "1");
      const { data } = await api.get<AdGroupsStatsResponse>(
        "/api/stats/ad-groups",
        { params }
      );
      return data.total;
    },
    staleTime: 60_000,
  });
}

export function useKeywordsCountForCampaigns(
  campaignIds: number[],
  statsFilters: StatsFilters,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["stats-keywords-count", campaignIds, statsFilters],
    enabled: enabled && campaignIds.length > 0,
    queryFn: async () => {
      const params = filtersToParams({
        ...statsFilters,
        campaign_ids: campaignIds,
      });
      params.set("limit", "1");
      const { data } = await api.get<KeywordsStatsResponse>(
        "/api/stats/keywords",
        { params }
      );
      return data.total;
    },
    staleTime: 60_000,
  });
}


// --- Keyword bid edit mutation -------------------------------------------

/**
 * PATCH /api/keywords/{id} returns a PendingWriteHandle (202 Accepted).
 * The backend's mirror flip + pending_writes enqueue means the UI can
 * optimistically reflect the new bid immediately. On success we
 * invalidate the keyword stats cache so the next tick pulls the synced
 * row.
 */
export function useUpdateKeyword() {
  const qc = useQueryClient();
  const prefixes = [
    ["stats-keywords"],
    ["stats-ad-groups"],
    ["stats-campaigns"],
  ] as const;
  return useMutation({
    mutationFn: async (args: { keyword_id: number; bid?: number; status?: "ENABLED" | "PAUSED" }) => {
      const { keyword_id, ...payload } = args;
      const { data } = await api.patch<PendingWriteHandle>(
        `/api/keywords/${keyword_id}`,
        payload
      );
      return data;
    },
    onMutate: async (args) => {
      await Promise.all(
        prefixes.map((p) => qc.cancelQueries({ queryKey: p }))
      );
      const patch: Record<string, unknown> = {};
      if (args.bid !== undefined) patch.bid = String(args.bid);
      if (args.status !== undefined) patch.status = args.status;
      const snapshots = prefixes.flatMap((p) =>
        patchCachedRows<{ keyword_id?: number }>(
          qc,
          p,
          (r) => r.keyword_id === args.keyword_id,
          patch
        )
      );
      return { snapshots };
    },
    onError: (_err, _args, context) => {
      if (context?.snapshots) restoreCachedRows(qc, context.snapshots);
    },
    onSuccess: (handle) => {
      // The 1.5s poller picks this id up and invalidates keyword
      // stats the moment the drainer flips it to synced — usually
      // within 1-2 seconds end-to-end vs. the old 4s+12s blind
      // refetch (incident 2026-05-04: status reverted on reload).
      if (handle.pending_write_id) {
        trackPendingWrite(handle.pending_write_id, "keyword");
      }
    },
    onSettled: () => {
      scheduleConfirmationRefetch(qc, prefixes);
    },
  });
}

// --- Ad Group mirror (config columns for the Ad Groups tab) --------------

/** GET /api/ad-groups?campaign_id= returns the *mirror* view — config
 *  fields like cpa_goal / translation / status, not metrics. Use in
 *  tandem with /api/stats/ad-groups when a tab needs both. */
export function useAdGroupsMirror(campaignId: number | null) {
  return useQuery({
    queryKey: ["ad-groups-mirror", campaignId],
    enabled: campaignId !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (campaignId !== null) params.set("campaign_id", String(campaignId));
      const { data } = await api.get<AdGroupMirrorRow[]>("/api/ad-groups", { params });
      return data;
    },
    staleTime: 60_000,
  });
}

// --- Negative keywords ---------------------------------------------------

export function useNegativeKeywords(
  campaignId: number | null,
  opts: { scope?: "all" | "campaign" | "ad_group"; ad_group_id?: number | null } = {}
) {
  return useQuery({
    queryKey: ["negative-keywords", campaignId, opts],
    enabled: campaignId !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("scope", opts.scope ?? "all");
      if (opts.ad_group_id != null) params.set("ad_group_id", String(opts.ad_group_id));
      const { data } = await api.get<NegativeKeywordRow[]>(
        `/api/campaigns/${campaignId}/negative-keywords`,
        { params }
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/** Add search terms as negative keywords (Search Terms tab). One pending
 *  write per target; the worker pushes to ASA + mirrors. */
export function useCreateNegativeKeywords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      texts: string[];
      match_type: "EXACT" | "BROAD";
      targets: { campaign_id: number; ad_group_id?: number | null }[];
    }) => {
      const { data } = await api.post<{ pending_write_ids: number[] }>(
        "/api/negative-keywords",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["negative-keywords"] });
    },
  });
}

/** Bulk-delete negative keywords (Negatives tab). One pending write; the
 *  worker deletes each via ASA, then re-reads the negatives list per scope
 *  and rewrites the mirror. Returns the pending_write_ids so the caller can
 *  await ASA confirmation, then invalidate to re-read the fresh list. */
export function useDeleteNegativeKeywords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { negative_keyword_ids: number[] }) => {
      const { data } = await api.delete<{ pending_write_ids: number[] }>(
        "/api/negative-keywords",
        { data: body },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["negative-keywords"] });
    },
  });
}

// --- Search terms --------------------------------------------------------

type SearchTermsQueryArgs = {
  date_from: string;
  date_to: string;
  ad_group_id?: number | null;
  search?: string;
  limit?: number;
  offset?: number;
  /** «Split by devices» — each row gets `devices` sub-rows. */
  split?: boolean;
};

export function useSearchTerms(
  campaignId: number | null,
  args: SearchTermsQueryArgs
) {
  return useQuery({
    queryKey: ["search-terms", campaignId, args],
    // A sort / page / search change makes a new query key; without this the
    // rows vanish, the table unmounts and the horizontal scroll snaps back
    // to the first column. Keep the previous rows until the new ones land.
    placeholderData: (prev) => prev,
    enabled: campaignId !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("date_from", args.date_from);
      params.set("date_to", args.date_to);
      if (args.ad_group_id != null) params.set("ad_group_id", String(args.ad_group_id));
      if (args.search) params.set("search", args.search);
      if (args.limit !== undefined) params.set("limit", String(args.limit));
      if (args.offset !== undefined) params.set("offset", String(args.offset));
      if (args.split) params.set("split", "devices");
      const { data } = await api.get<SearchTermsResponse>(
        `/api/campaigns/${campaignId}/search-terms`,
        { params }
      );
      return data;
    },
    staleTime: 60_000,
  });
}

// --- Ads -----------------------------------------------------------------

export function useAds(adGroupId: number | null) {
  return useQuery({
    queryKey: ["ads", adGroupId],
    enabled: adGroupId !== null,
    queryFn: async () => {
      const { data } = await api.get<AdRow[]>(`/api/ad-groups/${adGroupId}/ads`);
      return data;
    },
    staleTime: 60_000,
  });
}

// --- Per-CPP campaign rollup (Ads tab) ---------------------------

export type CampaignCppRow = {
  creative_id: number;
  product_page_id: string | null;
  cpp_name: string;
  creative_type: string;
  ad_status: "Active" | "Paused";
  ad_id: number;
  ad_group_ids: number[];
  ad_group_names: string[];
  ad_group_count_in_scope: number;
  total_ad_groups_in_campaign: number;
  is_all_ad_groups: boolean;
  screenshots: string[];
  screenshots_all: string[];
  impressions: number;
  taps: number;
  installs: number;
  spend_usd: string;
  total_installs: number;
  revenue_usd: string;
  cpm: string | null;
  ipm: number | null;
  ttr: number | null;
  avg_cpt: string | null;
  avg_cpa: string | null;
  roas: number | null;
};

export type CampaignCppsTotals = {
  impressions: number;
  taps: number;
  installs: number;
  spend_usd: string;
  total_installs: number;
  revenue_usd: string;
  cpm: string | null;
  ipm: number | null;
  ttr: number | null;
  avg_cpt: string | null;
  avg_cpa: string | null;
  roas: number | null;
};

export type CampaignCppsResponse = {
  campaign_id: number;
  ad_group_id: number | null;
  rows: CampaignCppRow[];
  totals: CampaignCppsTotals | null;
};

/** Per-CPP rollup for the Ads tab. Scope = whole campaign or one
 *  ad_group (drill-down inside CampaignDetailPage). Returns one row
 *  per Custom Product Page that's currently or historically present
 *  in scope during the date window. */
export function useCampaignCpps(args: {
  campaignId: number | null;
  adGroupId: number | null;
  dateFrom: string;
  dateTo: string;
}) {
  return useQuery({
    queryKey: [
      "campaign-cpps",
      args.campaignId,
      args.adGroupId,
      args.dateFrom,
      args.dateTo,
    ],
    enabled: args.campaignId !== null,
    queryFn: async () => {
      const params = new URLSearchParams({
        campaign_id: String(args.campaignId),
        date_from: args.dateFrom,
        date_to: args.dateTo,
      });
      if (args.adGroupId !== null) {
        params.set("ad_group_id", String(args.adGroupId));
      }
      const { data } = await api.get<CampaignCppsResponse>(
        `/api/stats/campaign-cpps?${params.toString()}`,
      );
      return data;
    },
    staleTime: 60_000,
  });
}

// ===========================================================================
// Settings — Apps CRUD
// ===========================================================================

export function useCreateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      bundle_id: string;
      name: string;
      platform: "ios" | "android";
      asa_app_id?: number | null;
      adapty_app_id?: string | null;
    }) => {
      const { data } = await api.post<App>("/api/apps", body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

export function useUpdateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      name?: string;
      asa_app_id?: number | null;
      adapty_app_id?: string | null;
      is_active?: boolean;
    }) => {
      const { id, ...body } = args;
      const { data } = await api.patch<App>(`/api/apps/${id}`, body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

export function useDeleteApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/apps/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

// ===========================================================================
// Settings — Users admin
// ===========================================================================

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data } = await api.get<UserRow[]>("/api/users");
      return data;
    },
    staleTime: 30_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    // ★ Шлём ТОЛЬКО `role`. Старое `account_admin` схема ещё принимает,
    // но при пришедшем `role` игнорирует — послать оба значило бы завести
    // второй источник той же правды, который однажды разойдётся.
    mutationFn: async (body: {
      email: string;
      full_name: string;
      role: AssignableRole;
      app_access: AppAccessItem[];
    }) => {
      const { data } = await api.post<UserCreateResponse>("/api/users", body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (user_id: number) => {
      const { data } = await api.post<UserCreateResponse>(
        `/api/users/${user_id}/resend-invitation`
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      full_name?: string | null;
      role?: AssignableRole;
      app_access?: AppAccessItem[];
      is_active?: boolean;
    }) => {
      const { id, ...body } = args;
      const { data } = await api.patch<UserRow>(`/api/users/${id}`, body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/users/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// ===========================================================================
// Settings — Subscription-provider integrations (Adapty & friends)
// ===========================================================================

export function useProviderCatalogue() {
  return useQuery({
    queryKey: ["providers"],
    queryFn: async () => {
      const { data } = await api.get<ProviderDescriptor[]>("/api/providers");
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useIntegrations() {
  return useQuery({
    queryKey: ["integrations"],
    queryFn: async () => {
      const { data } = await api.get<IntegrationSummary[]>("/api/integrations");
      return data;
    },
    staleTime: 30_000,
  });
}

/** Adapty apps with their own webhook URL (Settings → Integrations). */
export function useAdaptyWebhookApps(enabled: boolean) {
  return useQuery({
    queryKey: ["adapty-webhook-apps"],
    queryFn: async () => {
      const { data } = await api.get<AdaptyWebhookApp[]>("/api/integrations/adapty/apps");
      return data;
    },
    enabled,
    staleTime: 10_000,
    // The status changes in Adapty's tab, not ours: refresh on return and on
    // a timer, so "connected" shows up without a reload.
    refetchOnWindowFocus: true,
    refetchInterval: (q) => adaptyPollMs(q.state.data ?? []),
  });
}

export function useSaveAdaptyWebhookApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: number | null; body: AdaptyWebhookAppSave }) => {
      const { data } = args.id == null
        ? await api.post<AdaptyWebhookApp>("/api/integrations/adapty/apps", args.body)
        : await api.put<AdaptyWebhookApp>(`/api/integrations/adapty/apps/${args.id}`, args.body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["adapty-webhook-apps"] });
      // A new Android row joins the app catalogue.
      void qc.invalidateQueries({ queryKey: ["apps"] });
    },
  });
}

export function useCreateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      provider_code: string;
      config: Record<string, unknown>;
      enabled_modes: string[];
    }) => {
      const { data } = await api.post<IntegrationSummary>("/api/integrations", body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useUpdateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      config?: Record<string, unknown> | null;
      enabled_modes?: string[] | null;
    }) => {
      const { id, ...body } = args;
      const { data } = await api.patch<IntegrationSummary>(
        `/api/integrations/${id}`,
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/integrations/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useTestIntegrationConnection() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ConnectionTestResponse>(
        `/api/integrations/${id}/test-connection`
      );
      return data;
    },
  });
}

// ===========================================================================
// Settings — Event mapping
// ===========================================================================

export function useEventMapping(integrationId: number | null) {
  return useQuery({
    queryKey: ["event-mapping", integrationId],
    enabled: integrationId !== null,
    queryFn: async () => {
      const { data } = await api.get<EventMappingResponse>(
        `/api/integrations/${integrationId}/event-mapping`
      );
      return data;
    },
    staleTime: 30_000,
  });
}

export function useSaveEventMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      integration_id: number;
      entries: { native_event_type: string; canonical_code: string | null }[];
      confirm: boolean;
    }) => {
      const { data } = await api.put<EventMappingResponse>(
        `/api/integrations/${args.integration_id}/event-mapping`,
        { entries: args.entries, confirm: args.confirm }
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ["event-mapping", vars.integration_id] });
      void qc.invalidateQueries({ queryKey: ["integrations"] });
      // Derived metrics unlock once mapping is confirmed — nuke everything
      // so the next stats fetch picks up the new gate state.
      void qc.invalidateQueries({ queryKey: ["overview"] });
      void qc.invalidateQueries({ queryKey: ["stats-campaigns"] });
      void qc.invalidateQueries({ queryKey: ["stats-ad-groups"] });
      void qc.invalidateQueries({ queryKey: ["stats-keywords"] });
    },
  });
}

// ===========================================================================
// Settings — ASA integration (structurally separate from other providers)
// ===========================================================================

export function useAsaIntegration() {
  return useQuery({
    queryKey: ["asa-integration"],
    queryFn: async () => {
      // The endpoint now always returns a record — it lazily mints the
      // ES256 key pair for this org on first read, so the response
      // includes `public_key_pem` even before any Apple-side IDs are
      // saved. No 404 path anymore.
      const { data } = await api.get<AsaIntegrationResponse>("/api/integrations/asa");
      return data;
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useSaveAsaIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      client_id: string;
      team_id: string;
      key_id: string;
      asa_org_id: number;
    }) => {
      const { data } = await api.put<AsaIntegrationResponse>(
        "/api/integrations/asa",
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["asa-integration"] });
    },
  });
}

export function useRegenerateAsaKeypair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<AsaIntegrationResponse>(
        "/api/integrations/asa/regenerate-keypair"
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["asa-integration"] });
    },
  });
}

export function useDeleteAsaIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete("/api/integrations/asa");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["asa-integration"] });
    },
  });
}

export function useTestAsaConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ConnectionTestResponse>(
        "/api/integrations/asa/test-connection"
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["asa-integration"] });
    },
  });
}

// --- ASA billing (credit line → budget order / LOC invoice) --------------

export function useAsaBilling() {
  return useQuery({
    queryKey: ["asa-billing"],
    queryFn: async () => {
      const { data } = await api.get<AsaBillingResponse>(
        "/api/integrations/asa/billing"
      );
      return data;
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useSaveAsaBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AsaBillingRequest) => {
      const { data } = await api.put<AsaBillingResponse>(
        "/api/integrations/asa/billing",
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["asa-billing"] });
    },
  });
}

/** Live list of the ASA account's budget orders for the credit-line
 *  dropdown. Only enabled when the caller wants it (budget-order mode)
 *  — it makes a live ASA round-trip. */
export function useAsaBudgetOrders(enabled: boolean) {
  return useQuery({
    queryKey: ["asa-budget-orders"],
    queryFn: async () => {
      const { data } = await api.get<BudgetOrderOption[]>(
        "/api/integrations/asa/budget-orders"
      );
      return data;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// ASA Campaign Groups (orgIds). One account's credentials can have the role on
// several; we sync the primary + any enabled here.
export type AsaCampaignGroup = {
  asa_org_id: number;
  org_name: string | null;
  currency: string | null;
  payment_model: string | null;
  is_primary: boolean;
  enabled: boolean;
  access_level: AsaAccessLevel;
  /** Подключён в другой организации OneAds — приходит только из Discover. */
  blocked: boolean;
};

export function useAsaCampaignGroups() {
  return useQuery({
    queryKey: ["asa-campaign-groups"],
    queryFn: async () => {
      const { data } = await api.get<{ groups: AsaCampaignGroup[] }>(
        "/api/integrations/asa/campaign-groups"
      );
      return data.groups;
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useDiscoverAsaCampaignGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ groups: AsaCampaignGroup[] }>(
        "/api/integrations/asa/campaign-groups/discover"
      );
      return data.groups;
    },
    // Ответ Discover несёт и занятые другой организацией аккаунты (blocked) —
    // их нет в сохранённом списке, поэтому кладём ответ в кеш как есть.
    onSuccess: (groups) => {
      qc.setQueryData(["asa-campaign-groups"], groups);
      void qc.invalidateQueries({ queryKey: ["asa-integration"] });
    },
  });
}

// --- Sign in with Apple (ТЗ apple_signin_connect §7) ----------------------

export function useStartAppleOAuth() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ authorize_url: string }>(
        "/api/integrations/asa/oauth/start"
      );
      return data.authorize_url;
    },
  });
}

export function useApplePending(pendingId: string | null) {
  return useQuery({
    queryKey: ["apple-oauth-pending", pendingId],
    queryFn: async () => {
      const { data } = await api.get<{ id: string; accounts: AppleOAuthAccount[] }>(
        `/api/integrations/asa/oauth/pending/${pendingId}`
      );
      return data;
    },
    enabled: !!pendingId,
    staleTime: 0,
    retry: false,
  });
}

export function useConnectApplePending() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { pendingId: string; selected: number[]; primary: number }) => {
      const { data } = await api.post<AsaIntegrationResponse>(
        `/api/integrations/asa/oauth/pending/${v.pendingId}/connect`,
        { selected_asa_org_ids: v.selected, primary_asa_org_id: v.primary }
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["asa-integration"], data);
      void qc.invalidateQueries({ queryKey: ["asa-campaign-groups"] });
      void qc.invalidateQueries({ queryKey: ["asa-read-only-campaigns"] });
    },
  });
}

export function useCancelApplePending() {
  return useMutation({
    mutationFn: async (pendingId: string) => {
      await api.delete(`/api/integrations/asa/oauth/pending/${pendingId}`);
    },
  });
}

/** Кампании в группах с доступом только на чтение — правки по ним выключены. */
export function useReadOnlyCampaigns() {
  return useQuery({
    queryKey: ["asa-read-only-campaigns"],
    queryFn: async () => {
      const { data } = await api.get<{ campaign_ids: number[] }>(
        "/api/integrations/asa/read-only-campaigns"
      );
      return new Set(data.campaign_ids);
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useSetAsaCampaignGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (asaOrgIds: number[]) => {
      const { data } = await api.put<{ groups: AsaCampaignGroup[] }>(
        "/api/integrations/asa/campaign-groups",
        { asa_org_ids: asaOrgIds }
      );
      return data.groups;
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["asa-campaign-groups"] }),
  });
}

// ===========================================================================
// Overview — saved dashboard views
// ===========================================================================

export function useDashboardViews(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["dashboard-views", appId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appId != null) params.set("app_id", String(appId));
      const { data } = await api.get<DashboardView[]>(
        "/api/dashboard-views",
        { params }
      );
      return data;
    },
    staleTime: 30_000,
  });
}

export function useSaveDashboardView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      app_id: number | null;
      config: OverviewViewConfig;
      visibility: "private" | "shared";
      set_default?: boolean;
    }) => {
      const { data } = await api.post<DashboardView>(
        "/api/dashboard-views",
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dashboard-views"] });
    },
  });
}

export function useUpdateDashboardView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      name?: string;
      config?: OverviewViewConfig;
      visibility?: "private" | "shared";
      is_default?: boolean;
    }) => {
      const { id, ...body } = args;
      const { data } = await api.patch<DashboardView>(
        `/api/dashboard-views/${id}`,
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dashboard-views"] });
    },
  });
}

export function useDeleteDashboardView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/dashboard-views/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dashboard-views"] });
    },
  });
}

// --- Lightweight campaign mirror (asa_campaign_meta) -------------------------
//
// Reads `/api/campaigns` — pure asa_campaign_meta SELECT, no joins, no
// stats SQL, no cohort tables. Returns name + status + countries + the
// usual meta fields. Useful when you need to LIST campaigns without
// the heavy aggregation pipeline behind `useCampaignsTable` (which
// joins keyword_daily_stats + keyword_cohort_revenue + per-event
// rollups). Tens-of-ms vs full seconds on orgs with ~500 campaigns.
//
// Used by the Rules → Apply To modal's Campaigns tab — selection-only
// UX has no need for performance metrics.

export type CampaignMirror = {
  campaign_id: number;
  org_id: number;
  app_id: number | null;
  name: string | null;
  status: string | null;
  daily_budget: string | null;
  placement: string | null;
  countries: string[] | null;
  ad_channel_type: string | null;
  display_status: string | null;
  sync_status: string;
  sync_error: string | null;
  last_synced_at: string | null;
  last_local_update: string | null;
};

export function useCampaignsList(appId?: number | null) {
  return useQuery({
    queryKey: ["campaigns-mirror", appId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appId != null) params.set("app_id", String(appId));
      const qs = params.toString();
      const { data } = await api.get<CampaignMirror[]>(
        `/api/campaigns${qs ? `?${qs}` : ""}`,
      );
      return data;
    },
    staleTime: 30_000,
  });
}


// --- Campaign / Ad Group bulk status mutations -------------------------------
//
// Pause and Activate buttons in the Ads Manager toolbar fire one
// PATCH per selected entity. ASA's per-resource update endpoint takes
// a single id, so for N selected we await Promise.all of N mutations.
// The backend (PATCH /api/campaigns/:id, /api/ad-groups/:id,
// /api/keywords/:id) queues into pending_writes; the worker drains
// them. UI gets a 202 Accepted per call.

export function useUpdateCampaign() {
  const qc = useQueryClient();
  const prefixes = [["stats-campaigns"]] as const;
  return useMutation({
    mutationFn: async (args: {
      campaign_id: number;
      status?: "ENABLED" | "PAUSED";
      name?: string;
      daily_budget?: number;
      target_cpa?: number;
    }) => {
      const { campaign_id, ...payload } = args;
      const { data } = await api.patch<PendingWriteHandle>(
        `/api/campaigns/${campaign_id}`,
        payload
      );
      return data;
    },
    onMutate: async (args) => {
      await Promise.all(
        prefixes.map((p) => qc.cancelQueries({ queryKey: p }))
      );
      const patch: Record<string, unknown> = {};
      if (args.daily_budget !== undefined)
        patch.daily_budget = String(args.daily_budget);
      if (args.target_cpa !== undefined)
        patch.target_cpa = String(args.target_cpa);
      if (args.name !== undefined) patch.campaign_name = args.name;
      if (args.status !== undefined) {
        patch.status = args.status;
        // StatusDot picks `display_status` over `status` (it's ASA's
        // deliverability state — RUNNING / ON_HOLD / etc). Without
        // patching display_status here too, the UI would keep showing
        // "Running" right after a pause until the collector caught
        // up. Mirror the new status optimistically; the next refetch
        // brings ASA's authoritative value.
        patch.display_status = args.status;
      }
      const snapshots = prefixes.flatMap((p) =>
        patchCachedRows<{ campaign_id?: number }>(
          qc,
          p,
          (r) => r.campaign_id === args.campaign_id,
          patch
        )
      );
      return { snapshots };
    },
    onError: (_err, _args, context) => {
      if (context?.snapshots) restoreCachedRows(qc, context.snapshots);
    },
    onSuccess: (handle) => {
      if (handle.pending_write_id) {
        trackPendingWrite(handle.pending_write_id, "campaign");
      }
    },
    onSettled: () => {
      scheduleConfirmationRefetch(qc, prefixes);
    },
  });
}

export function useUpdateAdGroup() {
  const qc = useQueryClient();
  // Include stats-campaigns: an ad-group status flip can move the
  // parent campaign's serving status (ON_HOLD ↔ RUNNING), so the
  // Campaign Detail header must refetch too.
  const prefixes = [
    ["stats-ad-groups"],
    ["ad-groups-mirror"],
    ["stats-campaigns"],
  ] as const;
  return useMutation({
    mutationFn: async (args: {
      ad_group_id: number;
      status?: "ENABLED" | "PAUSED";
      default_bid?: number;
      cpa_goal?: number;
    }) => {
      const { ad_group_id, ...payload } = args;
      const { data } = await api.patch<PendingWriteHandle>(
        `/api/ad-groups/${ad_group_id}`,
        payload
      );
      return data;
    },
    onMutate: async (args) => {
      await Promise.all(
        prefixes.map((p) => qc.cancelQueries({ queryKey: p }))
      );
      const patch: Record<string, unknown> = {};
      if (args.default_bid !== undefined)
        patch.default_bid = String(args.default_bid);
      if (args.cpa_goal !== undefined) patch.cpa_goal = String(args.cpa_goal);
      if (args.status !== undefined) patch.status = args.status;
      const snapshots = prefixes.flatMap((p) =>
        patchCachedRows<{ ad_group_id?: number }>(
          qc,
          p,
          (r) => r.ad_group_id === args.ad_group_id,
          patch
        )
      );
      return { snapshots };
    },
    onError: (_err, _args, context) => {
      if (context?.snapshots) restoreCachedRows(qc, context.snapshots);
    },
    onSuccess: (handle) => {
      if (handle.pending_write_id) {
        trackPendingWrite(handle.pending_write_id, "ad_group");
      }
    },
    onSettled: () => {
      scheduleConfirmationRefetch(qc, prefixes);
    },
  });
}

// --- /api/labels --------------------------------------------------------------

/** Optionally narrowed by entity_type. The Ads Manager passes the
 *  scope so a Campaigns-tab list never surfaces ad_group labels and
 *  vice versa. Calling without `entityType` returns every label
 *  (used by row-rendering in StatsTable, where each row already knows
 *  its own grain and the LabelsContext picks the right slice). */
export function useLabels(entityType?: "campaign" | "ad_group") {
  return useQuery({
    queryKey: ["labels", entityType ?? "all"],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (entityType) params.entity_type = entityType;
      const { data } = await api.get<Label[]>("/api/labels", { params });
      return data;
    },
    staleTime: 60_000,
  });
}

export function useCreateLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      name: string;
      entity_type: "campaign" | "ad_group";
    }) => {
      const { data } = await api.post<Label>("/api/labels", args);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["labels"] });
    },
  });
}

export function useDeleteLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/labels/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["labels"] });
    },
  });
}

export function useApplyLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      label_id: number;
      entity_type: "campaign" | "ad_group";
      entity_ids: number[];
    }) => {
      const { label_id, ...body } = args;
      const { data } = await api.post<Label>(
        `/api/labels/${label_id}/apply`,
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["labels"] });
    },
  });
}

export function useUnapplyLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      label_id: number;
      entity_type: "campaign" | "ad_group";
      entity_ids: number[];
    }) => {
      const { label_id, ...body } = args;
      const { data } = await api.post<Label>(
        `/api/labels/${label_id}/unapply`,
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["labels"] });
    },
  });
}

// --- /api/column-presets ------------------------------------------------------

export function useColumnPresets(tab: "campaigns" | "ad_groups" | "keywords" | "rules") {
  return useQuery({
    queryKey: ["column-presets", tab],
    queryFn: async () => {
      const { data } = await api.get<ColumnPreset[]>(
        `/api/column-presets?tab=${tab}`
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useCreateColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      tab: "campaigns" | "ad_groups" | "keywords" | "rules";
      name: string;
      columns: string[];
      widths?: Record<string, number>;
    }) => {
      const { data } = await api.post<ColumnPreset>(
        "/api/column-presets",
        args
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ["column-presets", vars.tab] });
    },
  });
}

export function useUpdateColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      name?: string;
      columns?: string[];
      widths?: Record<string, number>;
    }) => {
      const { id, ...body } = args;
      const { data } = await api.patch<ColumnPreset>(
        `/api/column-presets/${id}`,
        body
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["column-presets"] });
    },
  });
}

export function useDeleteColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/column-presets/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["column-presets"] });
    },
  });
}

/** Mark this preset as the current user's default for its tab. The
 *  backend upserts into `user_default_column_preset` keyed on
 *  (user, tab) — so this transparently REPLACES whatever was the
 *  previous default. Next page load will auto-apply this preset's
 *  columns + widths. */
export function useSetDefaultColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ColumnPreset>(
        `/api/column-presets/${id}/default`
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["column-presets"] });
    },
  });
}

/** Clear the user's default IF this preset is currently the default.
 *  No-op (200) if a different preset is the default — protects against
 *  accidental clear via double-click on the wrong star. */
export function useClearDefaultColumnPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete<ColumnPreset>(
        `/api/column-presets/${id}/default`
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["column-presets"] });
    },
  });
}

// --- /api/asa/custom-product-pages -------------------------------------------

/** Streaming CPP picker. Opens the NDJSON endpoint, parses one event
 *  per line, fills in screenshots as each CPP's apps.apple.com scrape
 *  resolves. The first `header` event lands within ~1 sec (just the
 *  ASA round-trip); each `screenshots` event lands as that CPP's
 *  scrape finishes (typical ~1–3 sec for the slowest of 70).
 *
 *  Returned shape mirrors useQuery enough that ApplyCppModal callers
 *  don't change: `{ data, isLoading, refresh }`. `refresh()` re-opens
 *  the stream with `?refresh=true` which evicts the server's
 *  screenshot cache for this app first.
 *
 *  Not built on top of TanStack Query — useQuery's queryFn is
 *  request/response, no streaming hook. We manage state with useState
 *  and consume the body via fetch().body.getReader(). Auth header is
 *  injected manually since axios isn't in the streaming path. */
type CppStreamRow = CustomProductPagesResponse["rows"][number];
export function useCustomProductPages(asaAppId: number | null | undefined) {
  const [rows, setRows] = React.useState<CppStreamRow[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  // Bumps every time refresh() is called; useEffect re-runs to open
  // a fresh stream with refresh=true.
  const [refreshTick, setRefreshTick] = React.useState(0);
  // A Refresh is consumed by the one open it triggers: switching the app
  // afterwards must read the stored list again, not go to Apple.
  const pendingRefreshRef = React.useRef(false);

  const open = React.useCallback(
    async (appId: number, forceRefresh: boolean) => {
      const ctrl = new AbortController();
      abortRef.current?.abort();
      abortRef.current = ctrl;
      if (forceRefresh) setIsRefreshing(true);
      else setIsLoading(true);
      setError(null);

      const url =
        `/api/asa/custom-product-pages/stream?app_id=${appId}` +
        (forceRefresh ? "&refresh=true" : "");
      try {
        // The one request that cannot use the axios interceptor — streaming
        // needs fetch — so it repeats the interceptor's contract by hand:
        // on 401, refresh once (sharing axios's in-flight attempt) and retry.
        // Note that only the OPENING is at risk: the backend checks the token
        // in a dependency before the generator starts, so a stream already
        // running is unaffected by the token expiring mid-flight.
        const send = (tok: string | null) =>
          fetch(url, {
            headers: tok ? { Authorization: `Bearer ${tok}` } : {},
            signal: ctrl.signal,
          });

        let resp = await send(getAccessToken());
        if (resp.status === 401) {
          const fresh = await ensureFreshAccessToken();
          if (fresh) resp = await send(fresh);
        }
        if (!resp.ok || !resp.body) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        // Buffer for incomplete trailing line — chunks don't align
        // with NDJSON line boundaries.
        let buf = "";
        // Key on product_page_id (or "default" sentinel for the
        // Default page row) — creative_id collides at 0 between the
        // Default page and any freshly-created CPP that Apple hasn't
        // wrapped in a Creative yet. Both rows would receive each
        // other's screenshots if we matched on creative_id.
        const screenshotsByPpid = new Map<string, string[]>();
        const ppidKey = (ppid: string | null | undefined): string =>
          ppid ?? "default";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl = buf.indexOf("\n");
          while (nl !== -1) {
            const line = buf.slice(0, nl);
            buf = buf.slice(nl + 1);
            nl = buf.indexOf("\n");
            if (!line) continue;
            try {
              const evt = JSON.parse(line);
              if (evt.event === "header") {
                const headerRows: CppStreamRow[] = evt.rows ?? [];
                // Re-apply any screenshots that arrived BEFORE the
                // header (race-impossible right now since the server
                // emits header first, but cheap belt-and-suspenders).
                for (const r of headerRows) {
                  const s = screenshotsByPpid.get(ppidKey(r.product_page_id));
                  if (s) r.screenshots = s;
                }
                setRows(headerRows);
              } else if (evt.event === "screenshots") {
                const key = ppidKey(evt.product_page_id as string | null);
                const sh = (evt.screenshots as string[]) ?? [];
                screenshotsByPpid.set(key, sh);
                // The same page read may also name its device shelves;
                // absent means "no new answer" — keep the cached one.
                const dc = evt.device_classes as string[] | undefined;
                setRows((prev) =>
                  prev.map((r) =>
                    ppidKey(r.product_page_id) === key
                      ? dc
                        ? { ...r, screenshots: sh, device_classes: dc }
                        : { ...r, screenshots: sh }
                      : r
                  )
                );
              } else if (evt.event === "error") {
                setError(String(evt.message ?? "stream error"));
              }
              // "done" → just falls through to loop end
            } catch {
              // Malformed line — skip; don't kill the whole stream
              // over one bad row.
            }
          }
        }
      } catch (e) {
        if ((e as { name?: string })?.name === "AbortError") return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (asaAppId == null) {
      setRows([]);
      return;
    }
    const force = pendingRefreshRef.current;
    pendingRefreshRef.current = false;
    void open(asaAppId, force);
    return () => {
      abortRef.current?.abort();
    };
  }, [asaAppId, refreshTick, open]);

  const refresh = React.useCallback(() => {
    if (asaAppId == null) return;
    pendingRefreshRef.current = true;
    setRefreshTick((n) => n + 1);
  }, [asaAppId]);

  return {
    data: { rows },
    isLoading,
    isRefreshing,
    error,
    refresh,
  };
}

/** POST /api/campaigns/bulk-apply-cpp — synchronously creates one Ad
 *  per ad group inside the picked campaigns, each pointing at the
 *  picked creative. Returns the per-ad-group failure list. */
export function useBulkApplyCpp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      campaign_ids?: number[];
      ad_group_ids?: number[];
      creative_id: number;
      creative_name: string;
      /** UUID — required when creative_id === 0 (CPP has no ASA
       *  Creative wrapper yet, backend mints one). */
      product_page_id?: string | null;
      /** ASA adamId — required alongside product_page_id when
       *  creative_id === 0. */
      asa_app_id?: number | null;
    }) => {
      // Токен придумывает клиент, потому что номер задания нужен бару
      // ДО того, как вернётся ответ, а ответ приезжает через минуты: по
      // два обращения к Apple на группу под ограничителем 1,5 запроса в
      // секунду. Пятнадцать групп — две минуты молчания.
      const token = crypto.randomUUID();
      const post = api.post<BulkApplyCppResponse>(
        "/api/campaigns/bulk-apply-cpp",
        {
          campaign_ids: args.campaign_ids ?? [],
          ad_group_ids: args.ad_group_ids ?? [],
          creative_id: args.creative_id,
          creative_name: args.creative_name,
          product_page_id: args.product_page_id ?? null,
          asa_app_id: args.asa_app_id ?? null,
          progress_token: token,
        }
      );
      // Ждём появления строки задания и отдаём её бару. Опрос
      // прекращается сам, как только номер найден: дальше бар
      // опрашивает задание по своему обычному пути.
      void (async () => {
        for (let i = 0; i < 20; i++) {
          try {
            const { data } = await api.get<{ job_id: number | null }>(
              "/api/cpp/apply-job",
              { params: { token } }
            );
            if (data.job_id) {
              trackCampaignStudioJob(data.job_id);
              return;
            }
          } catch {
            // Сеть моргнула — пробуем ещё; бар без номера просто не
            // покажется, применение это не затрагивает.
          }
          await new Promise((r) => setTimeout(r, 500));
        }
      })();
      const { data } = await post;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["stats-ad-groups"] });
      void qc.invalidateQueries({ queryKey: ["ad-groups-mirror"] });
      void qc.invalidateQueries({ queryKey: ["ads"] });
      void qc.invalidateQueries({ queryKey: ["campaign-cpps"] });
    },
  });
}

/** POST /api/campaigns/bulk-pause-cpp — pause one or more CPPs from
 *  the Ads-tab toolbar. Each target identifies an Ad indirectly via
 *  (campaign, ad_group, creative); the backend finds the live
 *  ENABLED Ad and PUTs status=PAUSED on ASA. Idempotent — already-
 *  paused targets count as success. */
export function useBulkPauseCpp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { targets: BulkPauseCppTarget[] }) => {
      const { data } = await api.post<BulkPauseCppResponse>(
        "/api/campaigns/bulk-pause-cpp",
        { targets: args.targets },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["stats-ad-groups"] });
      void qc.invalidateQueries({ queryKey: ["ad-groups-mirror"] });
      void qc.invalidateQueries({ queryKey: ["ads"] });
      void qc.invalidateQueries({ queryKey: ["campaign-cpps"] });
    },
  });
}


// ----- /api/campaigns/bulk-edit-bid-budget -----

export type BulkEditField =
  | "target_cpa"
  | "daily_budget"
  | "cpa_goal"
  | "default_bid"
  | "cpc_bid"
  | "bid";

export type BulkEditLevel = "campaign" | "ad_group" | "keyword";
export type BulkEditAction = "increase" | "decrease" | "set_new" | "switch_off";
export type BulkEditUnit = "percent" | "amount";

export type BulkEditRequest = {
  campaign_ids: number[];
  level: BulkEditLevel;
  fields: BulkEditField[];
  action: BulkEditAction;
  value: number | null;
  value_unit: BulkEditUnit;
  // In-campaign "Edit bids" restricts the edit to selected entities.
  // Omitted (campaign-level Edit Bid/Budget) → edits all under campaign.
  ad_group_ids?: number[];
  keyword_ids?: number[];
};

export type BulkEditResponse = {
  success_count: number;
  failure_count: number;
  total_attempted: number;
  field_label: string;
  errors: Array<{
    level: string;
    entity_id: number;
    field: string;
    old_value?: string | null;
    attempted_value?: string | null;
    error: string;
  }>;
};

/** POST /api/campaigns/bulk-edit-bid-budget — synchronous fan-out
 *  across campaigns / ad_groups / keywords for the Edit bid/budget
 *  modal. Affects ENABLED + PAUSED entities by design.
 *
 *  No optimistic patching here: the modal stays open after Apply
 *  showing a toast, and the user can fire multiple Applies in
 *  succession. We invalidate all 3 stats queries on success so the
 *  table refreshes once the modal is closed. */
export function useBulkEditBidBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: BulkEditRequest) => {
      const { data } = await api.post<BulkEditResponse>(
        "/api/campaigns/bulk-edit-bid-budget",
        args
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["stats-campaigns"] });
      void qc.invalidateQueries({ queryKey: ["stats-ad-groups"] });
      void qc.invalidateQueries({ queryKey: ["stats-keywords"] });
      void qc.invalidateQueries({ queryKey: ["ad-groups-mirror"] });
    },
  });
}

// ----- /api/campaigns/duplicate -----

export type DuplicateCampaignRequest = {
  campaign_ids: number[];
  /** Single-mode: caller may set { "<id>": "new name" } to override
   *  the auto-suffix. Multi-mode: omit / null. */
  new_name_overrides?: Record<string, string> | null;
  enable: boolean;
  include_keywords: boolean;
  include_negatives: boolean;
};

export type DuplicateCampaignResponse = {
  pending_write_ids: number[];
  accepted_campaign_ids: number[];
  rejected: Array<{ campaign_id: number; error: string }>;
  job_id?: number | null;
};

/** POST /api/campaigns/duplicate — bulk-duplicate one or more
 *  campaigns. Backend writes one pending_writes row per source; the
 *  drainer cascades through ASA. Modal closes on Apply, the existing
 *  pending-writes-tracker reports back when each source is done. */
export function useBulkDuplicateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: DuplicateCampaignRequest) => {
      const { data } = await api.post<DuplicateCampaignResponse>(
        "/api/campaigns/duplicate",
        args,
      );
      return data;
    },
    onSuccess: (data) => {
      // Show the Campaign Studio progress bar for the batch (same bar as
      // Flow 1/3) — it polls the job + reports status/finish. The bar's own
      // completion effect also invalidates the mirror keys.
      if (data?.job_id) trackCampaignStudioJob(data.job_id);
      // Refetch the campaigns list shortly — the new campaigns appear once
      // the drainer finishes its cascade + mirror-materialization. A
      // zero-traffic dupe renders from the *mirror* (campaigns-mirror), not
      // stats, so both key families must be invalidated (§45.4). Also a
      // delayed second pass so slower cascades still surface without a manual
      // refresh — the CampaignStudioProgressBar's own completion invalidation
      // covers the exact-finish case for the job-tracked path.
      const keys = [
        ["stats-campaigns"],
        ["campaigns-mirror"],
        ["ad-groups-mirror"],
        ["keywords-mirror"],
      ];
      for (const k of keys) void qc.invalidateQueries({ queryKey: k });
      setTimeout(() => {
        for (const k of keys) void qc.invalidateQueries({ queryKey: k });
      }, 30_000);
    },
  });
}

// --- /api/audit-log/activity (Autopilot → Activity log) -----------------

export type ActivitySource = "manual" | "automated" | "autopilot";
export type ActivityCategory =
  | "cpa" | "bid" | "budget" | "start" | "other";

export type ActivityLogFilters = {
  date_from?: string | null;
  date_to?: string | null;
  source?: ActivitySource | "all" | null;
  category?: ActivityCategory | "all" | null;
  limit?: number;
  offset?: number;
};

export type ActivityLogRow = {
  id: number;
  created_at: string;
  source: ActivitySource;
  category: ActivityCategory;
  changes: string;
  author: {
    kind: "user" | "rule" | "script";
    display: string;
    email?: string | null;
  };
  action: string;
  resource_type: string | null;
  resource_id: string | null;
};

export type ActivityLogResponse = {
  rows: ActivityLogRow[];
  total: number;
  counts_by_source: Record<string, number>;
  counts_by_category: Record<string, number>;
};

/** Autopilot → Activity log. Backend enriches each audit_log row with
 *  source/category/changes/author derivations and filters out the
 *  bulk-summary rows (per design — user wants per-resource lines).
 *  Read-by-any-authenticated-user. */
export function useActivityLog(filters: ActivityLogFilters) {
  return useQuery({
    queryKey: ["activity-log", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.source && filters.source !== "all") {
        params.set("source", filters.source);
      }
      if (filters.category && filters.category !== "all") {
        params.set("category", filters.category);
      }
      if (filters.limit !== undefined) {
        params.set("limit", String(filters.limit));
      }
      if (filters.offset !== undefined) {
        params.set("offset", String(filters.offset));
      }
      const { data } = await api.get<ActivityLogResponse>(
        "/api/audit-log/activity",
        { params },
      );
      return data;
    },
    staleTime: 15_000,
  });
}

// --- /api/audit-log/changes (страница изменений платформы) -------------------

/** Откуда прочитано изменение: сработавшее правило, очередь записи к
 *  Apple, синхронный ручной путь. */
export type ChangeSource = "rule" | "queue" | "manual";
export type ChangeOutcome = "applied" | "synced" | "error" | "recorded";

export type PlatformChangeRow = {
  ts: string;
  source: ChangeSource;
  row_id: number;
  entity_type: string | null;
  entity_id: string | null;
  entity_name: string | null;
  campaign_name: string | null;
  ad_group_name: string | null;
  field: string | null;
  before: string | null;
  after: string | null;
  action: string;
  author: { kind: "rule" | "user" | "system"; name: string; email: string | null };
  outcome: ChangeOutcome;
  details: Record<string, unknown> | null;
};

export type PlatformChangesResponse = {
  rows: PlatformChangeRow[];
  next_cursor: string | null;
};

/** Изменения платформы за период. Период ОБЯЗАТЕЛЕН — страница без него
 *  не отвечает на вопрос, ради которого её открывают.
 *
 *  Листание курсорное: `next_cursor` уносит на следующую страницу, и
 *  общего числа строк нет намеренно — считать его по объединению трёх
 *  дописываемых таблиц значит платить за цифру, которая устареет к
 *  следующему нажатию. */
export function usePlatformChanges(params: {
  dateFrom: string;
  dateTo: string;
  sources?: ChangeSource[] | null;
  limit?: number;
  cursor?: string | null;
}) {
  const sources = params.sources?.length ? params.sources.join(",") : null;
  return useQuery({
    queryKey: [
      "platform-changes",
      params.dateFrom,
      params.dateTo,
      sources,
      params.limit ?? 100,
      params.cursor ?? null,
    ],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set("date_from", params.dateFrom);
      sp.set("date_to", params.dateTo);
      if (sources) sp.set("sources", sources);
      sp.set("limit", String(params.limit ?? 100));
      if (params.cursor) sp.set("cursor", params.cursor);
      const { data } = await api.get<PlatformChangesResponse>(
        "/api/audit-log/changes",
        { params: sp },
      );
      return data;
    },
    // Короткое — админ на этой странице обычно разбирает что-то живое, и
    // устаревшие данные, пока драйнер дожимает свежие записи, путают.
    staleTime: 15_000,
  });
}

// --- /api/audit-log ----------------------------------------------------------

export type AuditLogFilters = {
  action_prefix?: string | null;
  resource_type?: string | null;
  user_id?: number | null;
  date_from?: string | null;
  date_to?: string | null;
};

/** Admin-only audit-log feed. Backend already scopes by JWT.org_id —
 *  no client-side org param needed. Tunable via filters; pagination
 *  off offset/limit.
 *
 *  staleTime is short (15 sec) because admins viewing the audit page
 *  are usually investigating something live, and outdated data while
 *  the worker drainer is processing recent writes would be confusing. */
export function useAuditLog(
  filters: AuditLogFilters,
  pagination: { limit: number; offset: number },
) {
  return useQuery({
    queryKey: [
      "audit-log",
      filters.action_prefix ?? null,
      filters.resource_type ?? null,
      filters.user_id ?? null,
      filters.date_from ?? null,
      filters.date_to ?? null,
      pagination.limit,
      pagination.offset,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.action_prefix) params.set("action_prefix", filters.action_prefix);
      if (filters.resource_type) params.set("resource_type", filters.resource_type);
      if (filters.user_id != null) params.set("user_id", String(filters.user_id));
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      params.set("limit", String(pagination.limit));
      params.set("offset", String(pagination.offset));
      const { data } = await api.get<AuditLogResponse>("/api/audit-log", { params });
      return data;
    },
    staleTime: 15_000,
  });
}

// --- /api/rules --------------------------------------------------------------

export function useRules(params?: { search?: string; enabled?: boolean }) {
  return useQuery({
    queryKey: ["rules", params?.search ?? "", params?.enabled ?? null],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (params?.search) sp.set("search", params.search);
      if (params?.enabled != null) sp.set("enabled", String(params.enabled));
      const { data } = await api.get<import("./types").RuleListResponse>(
        "/api/rules",
        { params: sp },
      );
      return data;
    },
    staleTime: 15_000,
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: import("./types").RuleCreateRequest) => {
      const { data } = await api.post<import("./types").Rule>(
        "/api/rules",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      patch: import("./types").RuleUpdateRequest;
    }) => {
      const { data } = await api.patch<import("./types").Rule>(
        `/api/rules/${args.id}`,
        args.patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/rules/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useBulkRuleAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: import("./types").RuleBulkActionRequest) => {
      const { data } = await api.post<
        import("./types").RuleBulkActionResponse
      >("/api/rules/bulk", body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useRunRuleNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<import("./types").Rule>(
        `/api/rules/${id}/run-now`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useRuleRuns(args: {
  rule_id?: number | null;
  limit: number;
  offset: number;
}) {
  return useQuery({
    queryKey: ["rule-runs", args.rule_id ?? "all", args.limit, args.offset],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set("limit", String(args.limit));
      sp.set("offset", String(args.offset));
      const path =
        args.rule_id != null
          ? `/api/rules/${args.rule_id}/runs`
          : "/api/rules/runs/all";
      const { data } = await api.get<import("./types").RuleRunsResponse>(
        path,
        { params: sp },
      );
      return data;
    },
    staleTime: 5_000,
    // Auto-refetch every 10s when any visible run is still in
    // 'queued' or 'running' — the drainer flips them to success /
    // partial_error / error within ~1-3 min, the user shouldn't have
    // to manually F5 to see the transition.
    refetchInterval: (q) => {
      const data = q.state.data as
        | import("./types").RuleRunsResponse
        | undefined;
      if (!data) return false;
      const inFlight = data.rows.some(
        (r) => r.status === "queued" || r.status === "running",
      );
      return inFlight ? 10_000 : false;
    },
  });
}

// --- /api/rules/{id}/applications — where the rule is attached ---------------

export function useRuleApplications(ruleId: number | null | undefined) {
  return useQuery({
    queryKey: ["rule-applications", ruleId],
    enabled: ruleId != null,
    queryFn: async () => {
      const { data } = await api.get<
        import("./types").RuleApplicationsListResponse
      >(`/api/rules/${ruleId}/applications`);
      return data;
    },
    staleTime: 30_000,
  });
}

export function useAttachRuleApplications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      rule_id: number;
      payload: import("./types").RuleApplicationAttachRequest;
    }) => {
      const { data } = await api.post<
        import("./types").RuleApplicationsListResponse
      >(`/api/rules/${args.rule_id}/applications`, args.payload);
      return data;
    },
    onSuccess: (_d, args) => {
      void qc.invalidateQueries({
        queryKey: ["rule-applications", args.rule_id],
      });
      // Bump applications_count on the parent rule list view.
      // RulesPage uses `useRulesV2` which keys on ["rules-v2", ...] —
      // invalidating the legacy ["rules"] key was a no-op for the
      // current UI (v1→v2 rename gap). User reported the count
      // didn't refresh after Apply-To Save until they reloaded.
      void qc.invalidateQueries({ queryKey: ["rules-v2"] });
    },
  });
}

export function useDetachRuleApplications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      rule_id: number;
      payload: import("./types").RuleApplicationDetachRequest;
    }) => {
      // axios.delete supports a body via config.data
      const { data } = await api.delete<
        import("./types").RuleApplicationsListResponse
      >(`/api/rules/${args.rule_id}/applications`, {
        data: args.payload,
      });
      return data;
    },
    onSuccess: (_d, args) => {
      void qc.invalidateQueries({
        queryKey: ["rule-applications", args.rule_id],
      });
      void qc.invalidateQueries({ queryKey: ["rules-v2"] });
    },
  });
}

// --- /api/rules/bulk-apply — Rules Manager popup (apply / stop) ---------------

export type RulesBulkApplySkip = {
  entity_id: number | null;
  entity_name: string | null;
  reason: string;
};

export type RulesBulkApplyRuleResult = {
  rule_id: number;
  rule_name: string;
  affected: number;
  skipped: RulesBulkApplySkip[];
  error: string | null;
};

export type RulesBulkApplyResponse = {
  action: string;
  results: RulesBulkApplyRuleResult[];
};

import type { RulesBulkJob } from "./rules-bulk-jobs";
export type { RulesBulkJob };

export type RulesBulkApplyRequest = {
  action: "apply" | "stop";
  entity_type: "campaign" | "ad_group" | "keyword" | "search_term";
  entity_ids?: number[];
  search_terms?: { campaign_id: number; ad_group_id: number; text: string }[];
  rule_ids: number[];
};

/** Rules Manager: завести фоновое Apply / Stop. Ответ приходит сразу —
 *  номер задания; итог показывает `RulesBulkJobsToaster`. */
export function useCreateRulesBulkJob() {
  return useMutation({
    mutationFn: async (payload: RulesBulkApplyRequest) => {
      const { data } = await api.post<{ already_in_progress: boolean; job: RulesBulkJob }>(
        "/api/rules/bulk-apply-jobs",
        payload,
      );
      return data;
    },
  });
}

// --- /api/orgs/{id}/legal — Settings → Organization tab -----------------------

export type OrgLegal = {
  id: number;
  name: string;
  legal_type: string | null;
  /** ISO-3166-1 alpha-2. Paddle rejects anything else. */
  country: string | null;
  contact_email: string | null;
  street: string | null;
  city: string | null;
  postal_code: string | null;
  vat_id: string | null;
  /** Server's verdict — the same one the payment flow will read. */
  billing_ready: boolean;
  invoice_kind: "company" | "individual" | "unknown";
  /** Without these no invoice can be issued to the customer's company. */
  missing_fields: string[];
  /** Not blocking, but their absence costs the customer something. */
  recommended_fields: string[];
};

export type BillingCountry = {
  code: string;
  name: string;
  postal_code_required: boolean;
  /**
   * What Paddle prints as the expected shape of a tax number there.
   * Null for the 83 countries they publish no format for — those accept
   * anything, because a made-up pattern would reject a real customer.
   */
  vat_example: string | null;
};

/**
 * Countries Paddle accepts as a billing address. Static on the server —
 * cached hard here because it changes about never.
 */
export function useBillingCountries() {
  return useQuery({
    queryKey: ["billing-countries"],
    queryFn: async () => {
      const { data } = await api.get<{ countries: BillingCountry[] }>(
        "/api/orgs/billing-countries",
      );
      return data.countries;
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function useOrgLegal(orgId: number | null | undefined) {
  return useQuery({
    queryKey: ["org-legal", orgId],
    enabled: orgId != null,
    queryFn: async () => {
      const { data } = await api.get<OrgLegal>(`/api/orgs/${orgId}/legal`);
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpdateOrgLegal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { orgId: number; patch: Partial<OrgLegal> }) => {
      const { data } = await api.patch<OrgLegal>(
        `/api/orgs/${args.orgId}/legal`,
        args.patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-legal"] });
    },
  });
}

// --- /auth/profile + /auth/email-change -------------------------------------

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: {
      full_name?: string | null;
      timezone?: string | null;
      phone_cc?: string | null;
      phone_number?: string | null;
      telegram?: string | null;
    }) => {
      const { data } = await api.patch("/auth/profile", patch);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["auth-me"] });
    },
  });
}

export function useEmailChangeInit() {
  return useMutation({
    mutationFn: async (args: { new_email: string; current_password: string }) => {
      await api.post("/auth/email-change/init", args);
    },
  });
}

export function useEmailChangeConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post("/auth/email-change/confirm", { code });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["auth-me"] });
    },
  });
}

// --- /api/app-settings — Settings → App Settings -----------------------------

export type UpliftRow = {
  id: number;
  app_id: number;
  country: string | null;
  keyword: string | null;
  campaign: string | null;
  day: number;
  value: string;
  kind: "ARPU" | "ARPPU";
  uploaded_at: string;
};

export type UpliftUploadRow = {
  app_id: number;
  country?: string | null;
  keyword?: string | null;
  campaign?: string | null;
  day: number;
  value: number | string;
  kind: "ARPU" | "ARPPU";
};

export type UpliftCoeffRow = {
  app_id: number;
  country: string | null;
  keyword: string | null;
  campaign: string | null;
  from_day: number;
  to_day: number;
  coefficient: string;
};

export type GoalRule = {
  id: number;
  app_id: number;
  goal_n: number;
  main_event: string | null;
  bid_increase_max_pct: number;
  bid_decrease_max_pct: number;
  // Goal preset carries only bid-mutation knobs (caps + main_event
  // tag). Targets live per-segment in org_app_segment_goals
  // (Uplift Coeff tab). Thresholds / counting windows / anti-flap
  // live at rule-condition level (rules-engine DSL).
  configured_at: string | null;
  created_at: string;
  updated_at: string;
};

export function useUpliftData(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["uplift-data", appId ?? null],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (appId != null) sp.set("app_id", String(appId));
      const { data } = await api.get<UpliftRow[]>("/api/app-settings/uplift-data", {
        params: sp,
      });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useUploadUpliftData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { rows: UpliftUploadRow[]; replace?: boolean }) => {
      const { data } = await api.post<{ inserted: number; deleted: number }>(
        "/api/app-settings/uplift-data",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["uplift-data"] });
      void qc.invalidateQueries({ queryKey: ["uplift-coefficients"] });
    },
  });
}

export function useUpliftCoefficients(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["uplift-coefficients", appId ?? null],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: UpliftCoeffRow[] }>(
        "/api/app-settings/uplift-coefficients",
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 30_000,
  });
}

export function useGoalRules(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["goal-rules", appId ?? null],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: GoalRule[] }>(
        "/api/app-settings/goal-rules",
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useUpdateGoalRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      goal_n: number;
      patch: Partial<{
        main_event: string;
        bid_increase_max_pct: number;
        bid_decrease_max_pct: number;
        configured: boolean;
      }>;
    }) => {
      const { data } = await api.patch<GoalRule>(
        `/api/app-settings/goal-rules/${args.goal_n}`,
        args.patch,
        { params: { app_id: args.app_id } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["goal-rules"] });
    },
  });
}

// --- Per-app goal-target unit + per-segment Goal values (migration 0055) ----

export type GoalMetric = "ARPU" | "ARPPU" | "ROAS";

export type AppGoalTarget = {
  app_id: number;
  goal_metric: GoalMetric;
  goal_day: number;
  configured_at: string | null;
  created_at: string;
  updated_at: string;
};

export type SegmentGoal = {
  id: number;
  app_id: number;
  country: string | null;
  campaign: string | null;
  goal_n: number;
  target_value: string;
  updated_at: string;
};

export function useAppGoalTarget(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["app-goal-target", appId ?? null],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<AppGoalTarget | null>(
        "/api/app-settings/goal-target",
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpsertAppGoalTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      goal_metric: GoalMetric;
      goal_day: number;
      confirmed?: boolean;
    }) => {
      const { app_id, ...body } = args;
      const { data } = await api.put<AppGoalTarget>(
        "/api/app-settings/goal-target",
        body,
        { params: { app_id } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["app-goal-target"] });
    },
  });
}

// --- Google Cloud Storage credentials (Adjust → GCS integration) ---------

export type AppGcsCredentials = {
  app_id: number;
  gcs_project_id: string;
  gcs_bucket_name: string;
  has_service_account: boolean;
  enabled: boolean;
  last_tested_at: string | null;
  last_test_status: string | null;
  updated_at: string | null;
};

export type GcsTestResult = {
  ok: boolean;
  detail: string | null;
  object_count: number | null;
};

export function useAppGcsCredentials(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["app-gcs-credentials", appId ?? null],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<AppGcsCredentials | null>(
        "/api/app-settings/gcs-credentials",
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 30_000,
  });
}

// Org-level: all GCS credential rows. Drives the "Connected" indicator
// on the Integrations tile (creds are per-app, the tile is per-org).
export function useAllAppGcsCredentials() {
  return useQuery({
    queryKey: ["app-gcs-credentials-all"],
    queryFn: async () => {
      const { data } = await api.get<AppGcsCredentials[]>(
        "/api/app-settings/gcs-credentials/all",
      );
      return data;
    },
    staleTime: 30_000,
  });
}

export function useUpsertAppGcsCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      gcs_project_id: string;
      gcs_bucket_name: string;
      service_account_json?: Record<string, unknown> | null;
    }) => {
      const { app_id, ...body } = args;
      const { data } = await api.put<AppGcsCredentials>(
        "/api/app-settings/gcs-credentials",
        body,
        { params: { app_id } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["app-gcs-credentials"] });
      void qc.invalidateQueries({ queryKey: ["app-gcs-credentials-all"] });
    },
  });
}

export function useTestGcsConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number }) => {
      const { data } = await api.post<GcsTestResult>(
        "/api/app-settings/gcs-credentials/test",
        null,
        { params: { app_id: args.app_id } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["app-gcs-credentials"] });
      void qc.invalidateQueries({ queryKey: ["app-gcs-credentials-all"] });
    },
  });
}

// --- Per-app cohort source config ----------------------------------------

export type CohortSource = "adapty" | "adjust";

export type AppCohortConfig = {
  app_id: number;
  source: CohortSource;
  events: string[];
  updated_at: string | null;
};

export function useAppCohortConfig(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["app-cohort-config", appId ?? null],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<AppCohortConfig | null>(
        "/api/app-settings/cohort-config",
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 30_000,
  });
}

export function useUpsertAppCohortConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      source: CohortSource;
      events: string[];
    }) => {
      const { app_id, ...body } = args;
      const { data } = await api.put<AppCohortConfig>(
        "/api/app-settings/cohort-config",
        body,
        { params: { app_id } },
      );
      return data;
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["app-cohort-config"] }),
  });
}

export function useSegmentGoals(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["segment-goals", appId ?? null],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: SegmentGoal[] }>(
        "/api/app-settings/segment-goals",
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 30_000,
  });
}

export function useUpsertSegmentGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      country: string | null;
      campaign: string | null;
      goal_n: number;
      target_value: number | string;
    }) => {
      const { app_id, ...body } = args;
      const { data } = await api.put<SegmentGoal>(
        "/api/app-settings/segment-goals",
        body,
        { params: { app_id } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["segment-goals"] });
    },
  });
}

export function useDeleteSegmentGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      country: string | null;
      campaign: string | null;
      goal_n: number;
    }) => {
      const { data } = await api.delete<{ deleted: number }>(
        "/api/app-settings/segment-goals",
        {
          params: {
            app_id: args.app_id,
            goal_n: args.goal_n,
            country: args.country ?? undefined,
            campaign: args.campaign ?? undefined,
          },
        },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["segment-goals"] });
    },
  });
}

// --- /api/notifications — Settings → Notifications ---------------------------

export type NotifSchedule =
  | { kind: "daily" }
  | { kind: "weekly"; weekday: string }
  | { kind: "monthly"; day_of_month: number }
  | { kind: "custom"; mode: "dates"; dates: number[] }
  | { kind: "custom"; mode: "ordinal"; ordinal_label: string; dates?: number[] };

export type NotificationRow = {
  id: number;
  org_id: number;
  user_id: number;
  name: string;
  type: "overview" | "changes" | "main" | "goal" | "ua" | "weekly" | "weekly_extended" | "autopilot_digest";
  channel: string;
  schedule: NotifSchedule;
  time_local: string;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
};

export type NotificationDelivery = {
  id: number;
  notification_id: number;
  org_id: number;
  started_at: string;
  finished_at: string | null;
  channel: string;
  recipient: string;
  status: "pending" | "delivered" | "failed";
  error_message: string | null;
  payload_summary: string | null;
  notification_name: string | null;
  notification_type: string | null;
};

export function useNotifications(mine = false) {
  return useQuery({
    queryKey: ["notifications", mine],
    queryFn: async () => {
      const { data } = await api.get<{ rows: NotificationRow[] }>(
        "/api/notifications",
        { params: mine ? { mine: "true" } : {} },
      );
      return data.rows;
    },
    staleTime: 15_000,
  });
}

export function useCreateNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      type: NotificationRow["type"];
      channel: string;
      schedule: NotifSchedule;
      time_local: string;
      enabled?: boolean;
    }) => {
      const { data } = await api.post<NotificationRow>(
        "/api/notifications",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useUpdateNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      patch: Partial<{
        name: string;
        type: NotificationRow["type"];
        channel: string;
        schedule: NotifSchedule;
        time_local: string;
        enabled: boolean;
      }>;
    }) => {
      const { data } = await api.patch<NotificationRow>(
        `/api/notifications/${args.id}`,
        args.patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/notifications/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useRunNotificationNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{
        delivery_id: number | null;
        status: string;
        error: string | null;
      }>(`/api/notifications/${id}/run-now`);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notification-deliveries"] });
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useNotificationDeliveries(args: {
  limit: number;
  offset: number;
}) {
  return useQuery({
    queryKey: ["notification-deliveries", args.limit, args.offset],
    queryFn: async () => {
      const { data } = await api.get<{
        rows: NotificationDelivery[];
        total: number;
      }>("/api/notifications/deliveries/all", {
        params: { limit: args.limit, offset: args.offset },
      });
      return data;
    },
    staleTime: 5_000,
  });
}

// --- /api/telegram — Settings → Notifications → Channels (Telegram) ----------

export type TelegramChatRow = {
  id: number;
  chat_id: number;
  type: "private" | "group" | "supergroup" | "channel";
  title: string;
  active: boolean;
  added_at: string;
};

export type TelegramBotStatus = {
  connected: boolean;
  is_platform: boolean;
  username: string | null;
  bot_id: number | null;
  last_validated_at: string | null;
  last_message_at: string | null;
  chats: TelegramChatRow[];
};

export function useTelegramStatus() {
  return useQuery({
    queryKey: ["telegram-status"],
    queryFn: async () => {
      const { data } = await api.get<TelegramBotStatus>("/api/telegram/status");
      return data;
    },
    staleTime: 30_000,
  });
}

export function useTelegramConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await api.post<TelegramBotStatus>(
        "/api/telegram/connect",
        { token },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
  });
}

export function useTelegramDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/api/telegram/disconnect");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
  });
}

export function useTelegramRefreshChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<TelegramBotStatus>(
        "/api/telegram/refresh-chats",
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
  });
}

export function useTelegramTestSend() {
  return useMutation({
    mutationFn: async (args: { chat_id: number; text?: string }) => {
      const { data } = await api.post<{ delivered: boolean; error: string | null }>(
        "/api/telegram/test-send",
        args,
      );
      return data;
    },
  });
}

export function useDeleteTelegramChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (chatDbId: number) => {
      await api.delete(`/api/telegram/chats/${chatDbId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
  });
}



// =============================================================================
// Rules v2 (migrations 0059/0060) — circles + goals + execution log
// =============================================================================
//
// These types/hooks are ADDITIVE — legacy `useRules`/`useCreateRule` and
// the matching `Rule` type in lib/types.ts represent the v1.5 shape and
// are temporarily broken at runtime (backend migrated to v2). The full
// CreateRuleModal + RuleLogsView rewrites land in Phase C next session.
//
// Until then, the rules page renders an empty table (no rules survive
// the migration) and the Create modal will 422 on submit. Backend
// schema + scheduling + queue are fully live.

export type RuleLevelV2 =
  | "campaign" | "ad_group" | "keyword" | "search_term";

export type GoalMetricUnit = "ARPU" | "ARPPU" | "ROAS";

export type GoalV2 = {
  id: number;
  app_id: number;
  name: string;
  goal_n: number;
  search_term_action:
    | "add_as_negative_campaign"
    | "add_as_negative_adgroup"
    | null;
  created_at: string;
  updated_at: string;
};

export type RuleConditionV2 = {
  metric: string;
  op: string;
  val?: string | number | null;
  val2?: string | number | null;
  metric_window?: number | null;
  metric_window_unit?: "hours" | "days" | "weeks" | "months";
  // How this condition joins to the PREVIOUS one. The first condition
  // in a circle has no combinator (null). For 2+ conditions every one
  // after the first must be "and" or "or" — the engine groups runs of
  // AND together and ORs the groups (AND binds tighter). Save is blocked
  // until the user picks one for each gap.
  combinator?: "and" | "or" | null;
};

export type RuleScheduleV2 = {
  interval_n: number;
  interval_unit: "hours" | "days" | "weeks" | "months";
  run_at_utc?: string | null;
};

export type RuleActionV2 = {
  object: string;
  action?: string | null;
  val?: string | number | null;
  cap?: string | number | null;
  scope?: "To Campaign" | "To Ad Group" | null;
};

export type CircleConfigV2 = {
  conditions: RuleConditionV2[];
  schedule?: RuleScheduleV2 | null;
  actions: RuleActionV2[];
};

export type EventSwitcherConfigV2 = {
  event_type: string;
  threshold: number;
};

/** Configurator payload for the Broad-to-SCAG preset — key names are a
 *  contract with the backend engine (PresetScagConfig in schemas/rules.py). */
export type PresetScagConfigV2 = {
  template_id: number;
  daily_budget: number;
  bid_mode: "manual" | "auto08";
  bid_value?: number | null;
  add_negative?: boolean;
  launch_immediately?: boolean;
  create_automatically?: boolean;
  custom_product_page_id?: string | null;
  apply_rule_ids?: number[];
  /** Приложение пресета — сервер проверяет по нему, что ECG свои. */
  app_id?: number | null;
  ecg?: BtseEcgConfig;
  labels?: BtseLabelsConfig;
};

/** ТЗ BTSE §1: отсутствие страны в overrides — «наследует default»,
 *  null — явное «No ECG». */
export type BtseEcgConfig = {
  enabled: boolean;
  default_rule_id: number | null;
  overrides: Record<string, number | null>;
};

export type BtseLabelsConfig = {
  enabled: boolean;
  label_ids: number[];
};

export type BtseEcgOption = {
  id: number;
  name: string;
  created_at: string | null;
  status: "active" | "disabled" | "deleted";
};

export function useBtseEcgOptions(appId: number | null, ids: number[]) {
  const key = [...new Set(ids)].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["btse-ecg-options", appId, key],
    enabled: appId != null,
    queryFn: async () => {
      const qs = new URLSearchParams({ app_id: String(appId) });
      for (const id of key) qs.append("ids", String(id));
      const { data } = await api.get<{ rules: BtseEcgOption[]; autofill: Record<string, number> }>(
        `/api/rules/presets/btse/ecg-options?${qs}`,
      );
      return data;
    },
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}

/** Search Term Cleaner preset config — only the auto/suggestion toggle;
 *  conditions + action + schedule live in circle_1_config. */
export type PresetCleanerConfigV2 = {
  create_automatically?: boolean;
};

/** Early Stage Guard. Клиент шлёт только ВВОД — блок расчёта (лестницы,
 *  alpha_step, статус) заполняет фоновая задача воркера, и сервер
 *  затирает его при каждом сохранении. */
export type PresetEsgConfigV2 = {
  // Приложение пресета. Хранится в конфиге, а не берётся из
  // `rule.app_id`: тот выводится из привязок и пуст, пока их нет.
  app_id?: number | null;
  target_cpi: number;
  cpa_enabled: boolean;
  target_cpa?: number | null;
  cpa_event?: string | null;
  border_events?: string[];
  border_n?: number | null;
  border_installs?: number | null;
  // Сколько событий CPA доказывают монетизацию: столько набрал — и ключ
  // выходит из-под контроля стоимости инсталла, пока стоимость события
  // держится в таргете. От допуска не зависит.
  cpi_amnesty_n?: number | null;
  tolerance_pct: 5 | 12 | 20;
  // Только на чтение.
  calc_status?: "computing" | "ready" | "failed";
  // Пороги устарели и пересчитываются в фоне, но пресет продолжает
  // работать на прежних: в отличие от `computing`, прогоны не стоят.
  calc_stale?: boolean | null;
  calc_error?: string | null;
  cpi_ladder?: number[];
  cpa_ladder?: number[];
};

/** Event Cost Guard (ТЗ §2). Клиент шлёт только ввод; лестницу и статус
 *  расчёта пишет калибратор воркера, сервер затирает их на сохранении. */
export type PresetEcgConfigV2 = {
  app_id: number;
  event: string;
  target_cpa: number;
  maturity_day: number;
  mode: "light" | "optimal" | "aggressive";
  history_days: number;
  // Только на чтение.
  calc_status?: "computing" | "ready" | "failed";
  calc_error?: string | null;
  cpa_ladders?: Record<string, number[]>;
  calib_share_d?: number | null;
  calib_target?: number | null;
};

export type EcgMaturityResponse = {
  /** `pending` — пара поставлена в очередь, воркер посчитает в течение минут. */
  status: "ready" | "pending";
  proposed_day: number | null;
  share_at_proposed: number | null;
  curve: { day: number; share: number }[];
  events: number;
  installs: number;
  computed_at: string | null;
};

const ECG_MATURITY_POLL_MS = 15_000;

/** День зрелости события (ТЗ ECG §4.3) — из кэша, который считает воркер.
 *  Пока `pending`, опрос раз в 15 с; поле дня при этом редактируемо и
 *  сохранение не ждёт. */
export function useEcgMaturity(appId: number | null, event: string) {
  return useQuery({
    queryKey: ["ecg-maturity", appId, event],
    enabled: appId != null && !!event,
    queryFn: async () => {
      const { data } = await api.get<EcgMaturityResponse>(
        "/api/rules/presets/ecg/maturity",
        { params: { app_id: appId, event } },
      );
      return data;
    },
    staleTime: 5 * 60_000,
    refetchInterval: (q) =>
      q.state.data?.status === "pending" ? ECG_MATURITY_POLL_MS : false,
  });
}

export type EcgSummaryResponse = {
  watched: number;
  on_target: number;
  correcting: number;
  /** Keys with no measurement yet (ECG fixes §5.4). */
  collecting: number;
  paused_by_rule: number;
  taken_over: number;
};

export type EcgTile = keyof EcgSummaryResponse;

/** Keyword ids behind one ECG card tile — the logs filter on tile click. */
export function useEcgTileKeys(ruleId: number | null, tile: EcgTile | null) {
  return useQuery({
    queryKey: ["ecg-tile-keys", ruleId, tile],
    enabled: ruleId != null && tile != null,
    queryFn: async () => {
      const { data } = await api.get<{ tile: string; keyword_ids: number[] }>(
        `/api/rules/${ruleId}/ecg/summary/${tile}`,
      );
      return data.keyword_ids;
    },
    staleTime: 30_000,
  });
}

export type EcgEffectDay = {
  offset: number;
  date: string;
  /** Hourly windows (ECG fixes §6.5): the 24 h span this row covers. */
  from?: string;
  to?: string;
  granularity?: "hourly" | "daily";
  available: boolean;
  bid: number | null;
  avg_cpt: number | null;
  impressions: number | null;
  taps: number | null;
  spend_usd: number | null;
  sov_slot1: number | null;
  sov_slot2: number | null;
  sov_total: number | null;
  sov_band: boolean;
  sov_shared: boolean | null;
  events: number | null;
  installs?: number | null;
  /** ECG fixes 3 §4.2: an "after" window is shown only when all 24 hours are in. */
  status?: "ok" | "collecting" | "missing" | null;
  missing_hours?: number | null;
};

export type EcgEffectBaseline = {
  /** Per-day averages (ECG fixes 3 §4.1); "per_day" on every row the API returns. */
  unit?: "per_day";
  days: number;
  from?: string;
  to?: string;
  bid: number | null;
  avg_cpt: number | null;
  impressions: number | null;
  taps: number | null;
  spend_usd: number | null;
  events: number | null;
  installs?: number | null;
  ttr?: number | null;
  sov_slot1: number | null;
  sov_slot2: number | null;
  sov_total: number | null;
  sov_band: boolean;
  sov_shared: boolean;
};

export type EcgEffectRow = {
  log_id: number;
  ts: string;
  keyword_id: number;
  keyword_text: string | null;
  source: "ecg" | "other_rule" | "manual" | "apple_console";
  source_rule: string | null;
  skip_reason: string | null;
  confirmed_at: string | null;
  anchor_at: string | null;
  anchor_source: "apple_confirmation" | "run_time";
  bid_before: number | null;
  bid_after: number | null;
  reason_text: string | null;
  baseline: EcgEffectBaseline | null;
  baseline_logged?: Record<string, unknown> | null;
  after: EcgEffectDay[];
};

export function useEcgBidEffect(ruleId: number | null, logId: number | null) {
  return useQuery({
    queryKey: ["ecg-bid-effect", ruleId, logId],
    enabled: ruleId != null && logId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: EcgEffectRow[] }>(
        `/api/rules/${ruleId}/ecg/bid-effect`,
        { params: { log_id: logId, limit: 1 } },
      );
      return data.rows[0] ?? null;
    },
    staleTime: 60_000,
  });
}

export function useEcgSummary(ruleId: number | null) {
  return useQuery({
    queryKey: ["ecg-summary", ruleId],
    enabled: ruleId != null,
    queryFn: async () => {
      const { data } = await api.get<EcgSummaryResponse>(
        `/api/rules/${ruleId}/ecg/summary`,
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/** Duplicate Keyword Guard — три параметра, всё остальное зашито (ТЗ §2).
 *  Охват — приложения целиком, привязок у правила нет. */
export type PresetDkgConfigV2 = {
  app_ids: number[];
  run_every_days: number;
  mode: "recommend" | "auto";
};

export type PresetConfigV2 =
  | PresetScagConfigV2
  | PresetCleanerConfigV2
  | PresetEsgConfigV2
  | PresetDkgConfigV2
  | PresetEcgConfigV2;

export type PresetKindV2 =
  | "broad_to_scag"
  | "search_term_cleaner"
  | "early_stage_guard"
  | "duplicate_keyword_guard"
  | "event_cost_guard"
  // Системное правило: плитки у него нет и конфигуратора тоже — оно ЕСТЬ у
  // каждой организации. В союзе нужно, потому что бэкенд его отдаёт: без
  // него значение не совпало бы ни с одной веткой разбора, и притом молча
  // (§96 — расхождение отдаваемого типа не даёт ни 422, ни исключения).
  | "exact_terms_cleaner";

export type EsgScopeEntity = {
  id: number;
  name: string;
  status: string | null;
  parent: string | null;
};

export type EsgScopeResponse = {
  campaigns: EsgScopeEntity[];
  ad_groups: EsgScopeEntity[];
  keywords: EsgScopeEntity[];
  /** Выдача упёрлась в потолок — показано не всё, нужен поиск. */
  campaigns_truncated: boolean;
  ad_groups_truncated: boolean;
  keywords_truncated: boolean;
};

/** Что можно привязать к Early Stage Guard у этого приложения.
 *  Фильтр (чистые EXACT-группы) держит сервер — повторять его здесь
 *  значило бы завести второе определение, которое разойдётся с первым.
 *  ПОИСК тоже серверный: выдача ограничена потолком, и отбор на клиенте
 *  искал бы внутри уже отрезанного куска (так и терялись кампании
 *  из хвоста алфавита). */
export function useEsgScope(appId: number | null, q: string = "") {
  return useQuery({
    queryKey: ["esg-scope", appId, q],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<EsgScopeResponse>(
        "/api/rules/presets/early-stage-guard/scope",
        { params: { app_id: appId, q } },
      );
      return data;
    },
    staleTime: 60_000,
    // Прежняя выдача остаётся на экране, пока летит запрос по новой
    // строке: без этого список моргает в пустоту на каждое слово.
    placeholderData: (prev) => prev,
  });
}

export type EsgIneligibleEntity = {
  entity_type: "campaign" | "ad_group" | "keyword";
  entity_id: number;
  name: string;
  /** Why the preset has nothing to evaluate there — server-worded. */
  reason: string;
};

/** Что из уже привязанного пресет не увидит.
 *
 *  Спрашивается ПОСЛЕ сохранения привязки: стандартное окно «Apply to»
 *  показывает обычные списки Ads Manager, и выбрать там можно широкую
 *  или смешанную кампанию, которую прогон потом молча пропустит.
 *  Определение области держит сервер — оно одно и то же у прогона, у
 *  подборщика и у этой проверки. */
export function usePresetScopeCheck() {
  return useMutation({
    mutationFn: async (ruleId: number) => {
      const { data } = await api.get<{ ineligible: EsgIneligibleEntity[] }>(
        `/api/rules/${ruleId}/preset-scope-check`,
      );
      return data.ineligible;
    },
  });
}

/** Нативные события ОДНОГО приложения (не всей организации). */
export function useAppEventTypes(appId: number | null) {
  return useQuery({
    queryKey: ["event-types", "app", appId],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: EventTypeOptionV2[] }>(
        "/api/event-types",
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 5 * 60_000,
  });
}

export type RuleV2 = {
  id: number;
  org_id: number;
  app_id: number | null;
  name: string;
  entity_level: RuleLevelV2;
  goal_id: number | null;
  circle_1_config: CircleConfigV2 | Record<string, unknown>;
  circle_2_config: CircleConfigV2 | Record<string, unknown> | null;
  circle_2_action_based_cohort: boolean;
  circle_2_event_switcher: EventSwitcherConfigV2 | null;
  version: number;
  enabled: boolean;
  run_once: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
  /** Preset instance marker — "broad_to_scag" for SCAG-extractor
   *  instances, null for regular rules. Edit on a preset rule opens
   *  the preset configurator, not CreateRuleModal. */
  preset?: string | null;
  preset_config?: PresetConfigV2 | null;
  /** Приложение правила, выведенное СЕРВЕРОМ из трёх источников:
   *  preset_config.app_id → rules.app_id → приложение привязок.
   *  Конфигуратор пресета открывается по нему, чтобы не собирать
   *  цепочку на клиенте и не оставаться с пустым полем. */
  resolved_app_id?: number | null;
  /** Server-side aggregate from rule_applications. 0 for new rules
   *  with no entities attached. Drives the "Apply It To" column. */
  applications_count: number;
  /** {entity_type: count} of actual attachments — the column names
   *  what the rule is applied TO (a search-term rule attached to
   *  campaigns reads "8 campaigns", not "8 search terms"). */
  applications_by_type?: Record<string, number>;
  /** fix2: entities of the level asked with `count_level` the rule is
   *  applied to — the Ads Manager column's own definition. Absent
   *  (null) unless the list was asked with `count_level`. */
  covered_count?: number | null;
};

export type RuleExecutionLogEntryV2 = {
  id: number;
  ts: string;
  rule_id: number | null;
  rule_id_snapshot: number;
  rule_version: number;
  rule_name_snapshot: string | null;
  entity_type: string;
  entity_id: number | null;
  circle: number;
  check_result: "no_match" | "matched" | "applied" | "error" | "skipped";
  skip_reason: string | null;
  metrics_snapshot: Record<string, unknown>;
  conditions_checked: Array<Record<string, unknown>>;
  goal_id: number | null;
  goal_achieved: boolean | null;
  goal_metric_value: number | null;
  goal_repeatedly_achieved: boolean;
  action_definition: Record<string, unknown> | null;
  action_actual_change: Record<string, unknown> | null;
  error_message: string | null;
  pending_write_id: number | null;
  // Names + breadcrumb resolved server-side via LEFT JOINs on the
  // meta tables. Lets the logs view render "UR_PK_NA / AG1 /
  // days since counter" instead of bare IDs.
  entity_name?: string | null;
  campaign_name?: string | null;
  ad_group_name?: string | null;
  /** Keyword match type (ECG fixes §5.3): "BROAD #id" in the Entity cell. */
  match_type?: string | null;
};

export type EventTypeOptionV2 = {
  value: string;
  label: string;
  canonical_code: string | null;
  category: string | null;
};

/** One quantity buildable on an event: count (`event:`), CPA
 * (`event_cpa:`), CR (`event_cr:`). Comes from the backend
 * (dsl.EVENT_METRIC_KINDS) so the modal can't offer a metric the rules
 * engine has no branch for — see app/services/rules/dsl.py. */
export type EventMetricKindV2 = {
  prefix: string;
  group_label: string;
  option_suffix: string;
  levels: string[];
};

// ------ Goals CRUD ------

export function useGoals(appId?: number | null) {
  return useQuery({
    queryKey: ["goals-v2", appId ?? null],
    queryFn: async () => {
      const params = appId != null ? { app_id: appId } : undefined;
      const { data } = await api.get<{ rows: GoalV2[] }>(
        "/api/goals",
        { params },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      name: string;
      goal_n: number;
      search_term_action?: GoalV2["search_term_action"];
    }) => {
      const { data } = await api.post<GoalV2>("/api/goals", args);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["goals-v2"] });
    },
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      goal_id: number;
      patch: Partial<{
        name: string;
        goal_n: number;
        search_term_action: GoalV2["search_term_action"];
      }>;
    }) => {
      const { data } = await api.patch<GoalV2>(
        `/api/goals/${args.goal_id}`,
        args.patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["goals-v2"] });
    },
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (goalId: number) => {
      await api.delete(`/api/goals/${goalId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["goals-v2"] });
    },
  });
}

// ------ Rules v2 CRUD ------

export function useRulesV2(params?: {
  search?: string;
  enabled?: boolean;
  app_id?: number;
  /** Adds `covered_count` per rule for that level (the filter's rule picker). */
  count_level?: "campaign" | "ad_group" | "keyword";
}) {
  return useQuery({
    queryKey: ["rules-v2", params],
    queryFn: async () => {
      const { data } = await api.get<{ rows: RuleV2[] }>(
        "/api/rules",
        { params },
      );
      return data.rows;
    },
    staleTime: 30_000,
  });
}

export function useCreateRuleV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id?: number | null;
      name: string;
      entity_level: RuleLevelV2;
      goal_id?: number | null;
      circle_1_config: CircleConfigV2;
      circle_2_config?: CircleConfigV2 | null;
      circle_2_action_based_cohort?: boolean;
      circle_2_event_switcher?: EventSwitcherConfigV2 | null;
      enabled?: boolean;
      run_once?: boolean;
      run_now?: boolean;
      preset?: PresetKindV2 | null;
      preset_config?: PresetConfigV2 | null;
    }) => {
      const { data } = await api.post<RuleV2>("/api/rules", args);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules-v2"] });
      void qc.invalidateQueries({ queryKey: ["btse-ecg-options"] });
    },
  });
}

export function useUpdateRuleV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      rule_id: number;
      patch: Partial<{
        name: string;
        goal_id: number | null;
        circle_1_config: CircleConfigV2;
        circle_2_config: CircleConfigV2 | null;
        circle_2_action_based_cohort: boolean;
        circle_2_event_switcher: EventSwitcherConfigV2 | null;
        enabled: boolean;
        run_once: boolean;
        run_now: boolean;
        preset_config: PresetConfigV2;
      }>;
    }) => {
      const { data } = await api.patch<RuleV2>(
        `/api/rules/${args.rule_id}`,
        args.patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules-v2"] });
    },
  });
}

export function useDeleteRuleV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: number) => {
      await api.delete(`/api/rules/${ruleId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules-v2"] });
    },
  });
}

export function useRunRuleNowV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: number) => {
      const { data } = await api.post<RuleV2>(
        `/api/rules/${ruleId}/run-now`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules-v2"] });
    },
  });
}

export function useDuplicateRuleV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { ruleId: number; name: string; enabled: boolean }) => {
      const { data } = await api.post<RuleV2>(`/api/rules/${args.ruleId}/duplicate`, {
        name: args.name,
        enabled: args.enabled,
      });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rules-v2"] });
    },
  });
}

// ------ Execution log (replaces rule_runs.log_json) ------

export function useRuleExecutionLog(args: {
  ruleId?: number | null;
  entityId?: number | null;
  limit?: number;
  offset?: number;
  /** ISO YYYY-MM-DD inclusive */
  dateFrom?: string | null;
  /** ISO YYYY-MM-DD inclusive */
  dateTo?: string | null;
  /** All-logs mode: filter by rule name (ILIKE on the backend). */
  search?: string | null;
  /** Only evaluations that actually changed something (applied). */
  changesOnly?: boolean;
  /** ECG card tile click: only these keywords (ECG fixes §5.4). */
  entityIds?: number[] | null;
}) {
  const url =
    args.ruleId != null
      ? `/api/rules/${args.ruleId}/logs`
      : "/api/rules/logs/all";
  return useQuery({
    queryKey: ["rules-v2-logs", args],
    enabled: true,
    queryFn: async () => {
      const params: Record<string, number | string | undefined> = {
        limit: args.limit ?? 50,
        offset: args.offset ?? 0,
      };
      if (args.entityId != null) params.entity_id = args.entityId;
      if (args.dateFrom) params.date_from = args.dateFrom;
      if (args.dateTo) params.date_to = args.dateTo;
      if (args.search && args.ruleId == null) params.search = args.search;
      if (args.changesOnly) params.changes_only = "true";
      if (args.entityIds && args.entityIds.length > 0) params.entity_ids = args.entityIds.join(",");
      const { data } = await api.get<{
        rows: RuleExecutionLogEntryV2[];
        total: number;
      }>(url, { params });
      return data;
    },
    staleTime: 5_000,
  });
}

// ------ Event types (for circle_2 switcher dropdown) ------

type EventTypesPayload = {
  rows: EventTypeOptionV2[];
  metric_kinds?: EventMetricKindV2[];
};

const eventTypesQueryOptions = {
  queryKey: ["event-types"] as const,
  queryFn: async () => {
    const { data } = await api.get<EventTypesPayload>("/api/event-types");
    return data;
  },
  staleTime: 5 * 60_000,
};

/** The event list — rows AND metric kinds — for one app
 *  (`/api/event-types?app_id=`), or the org-wide union when `appId` is
 *  null. The Ads Manager filter's metric list follows the app picker
 *  (its TZ §3.2); the rule modal keeps the org-wide `useEventTypes`. */
export function useEventTypesForFilter(appId: number | null) {
  return useQuery({
    queryKey: ["event-types", "filter", appId ?? "all"],
    queryFn: async () => {
      const { data } = await api.get<EventTypesPayload>("/api/event-types", {
        params: appId != null ? { app_id: appId } : {},
      });
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useEventTypes() {
  // Same cache entry as useEventMetricKinds, two views of it — the
  // dropdown needs both halves of one response.
  return useQuery({
    ...eventTypesQueryOptions,
    select: (d: EventTypesPayload) => d.rows,
  });
}

/** Which quantities (count / CPA / CR) the rule modal may build on an
 * event. Empty until the response carries them — degrades to a
 * count-only dropdown rather than guessing prefixes client-side. */
export function useEventMetricKinds() {
  return useQuery({
    ...eventTypesQueryOptions,
    select: (d: EventTypesPayload) => d.metric_kinds ?? [],
  });
}


// =============================================================================
// User alerts (in-dashboard Topbar bell, since migration 0061)
// =============================================================================

export type UserAlert = {
  id: number;
  org_id: number;
  kind: string;
  payload: Record<string, unknown>;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  dismissed_at: string | null;
  dismissed_by_user_id: number | null;
};

export function useUserAlerts(opts?: { unresolved?: boolean }) {
  const unresolved = opts?.unresolved ?? true;
  return useQuery({
    queryKey: ["user-alerts", { unresolved }],
    queryFn: async () => {
      const { data } = await api.get<{ rows: UserAlert[] }>(
        "/api/user-alerts",
        { params: { unresolved } },
      );
      return data.rows;
    },
    staleTime: 30_000,
  });
}

export function useDismissUserAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (alertId: number) => {
      await api.delete(`/api/user-alerts/${alertId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user-alerts"] });
    },
  });
}

export function useDismissAllUserAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<{ dismissed: number }>(
        "/api/user-alerts",
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user-alerts"] });
    },
  });
}

// --- Campaign Studio ------------------------------------------------------

/** Static-ish list of supported ASA countries — backend returns a
 *  sorted (alpha-2, display_name) list. Cached for 1h since it never
 *  changes mid-session. */
export function useCampaignStudioCountries(opts: { enabled: boolean }) {
  return useQuery({
    queryKey: ["cs-countries"],
    enabled: opts.enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data } = await api.get<CsCountry[]>(
        "/api/campaign-studio/countries",
      );
      return data;
    },
  });
}

/** Per-app campaign-name uniqueness check (mirror lookup). Called on
 *  blur from the name input — debounced caller-side. The hook itself
 *  is just a one-shot POST, no caching, so duplicates after backend
 *  state changes never go stale. */
export function useCampaignNameCheck() {
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      name: string;
      devices?: CsDeviceChoice;
      campaign_type?: string;
    }) => {
      const { data } = await api.post<CsNameCheckResponse>(
        "/api/campaign-studio/campaigns/check-name",
        args,
      );
      return data;
    },
  });
}

/** Flow 1 — single Create Campaign. Returns immediately with a job_id;
 *  the actual ASA cascade runs in the worker drainer. Frontend stashes
 *  the job_id in localStorage and starts polling via
 *  `useCampaignStudioJob`. */
export function useCreateCampaignFlow1() {
  return useMutation({
    mutationFn: async (payload: CsFlow1Request) => {
      const { data } = await api.post<CsJobCreateResponse>(
        "/api/campaign-studio/campaigns",
        payload,
      );
      return data;
    },
  });
}

/** Polls /jobs/{id} every 2s while the job is queued / running. Stops
 *  polling on terminal state. Matches the design brief §5.2 cadence. */
export function useCampaignStudioJob(jobId: number | null) {
  return useQuery({
    queryKey: ["cs-job", jobId],
    enabled: jobId !== null,
    // A stale localStorage job handle (job from a DIFFERENT org, or
    // one that was cleaned up) 404s. Don't retry it and don't keep
    // polling — otherwise it 404s every 2s forever and surfaces an
    // error. The progress bar clears the stale id on isError. See
    // CampaignStudioProgressBar.
    retry: false,
    refetchInterval: (q) => {
      if (q.state.status === "error") return false;
      const data = q.state.data as CsJobProgress | undefined;
      if (!data) return 2000;
      if (data.status === "queued" || data.status === "running") return 2000;
      return false;
    },
    queryFn: async () => {
      const { data } = await api.get<CsJobProgress>(
        `/api/campaign-studio/jobs/${jobId}`,
      );
      return data;
    },
  });
}

/** Final per-unit report — fired once the sticky progress bar shows
 *  terminal state and the user clicks "View details". */
export function useCampaignStudioJobReport(jobId: number | null) {
  return useQuery({
    queryKey: ["cs-job-report", jobId],
    enabled: jobId !== null,
    queryFn: async () => {
      const { data } = await api.get<CsJobReport>(
        `/api/campaign-studio/jobs/${jobId}/report`,
      );
      return data;
    },
  });
}

// --- Campaign Studio: Name Templates ---------------------------------------

export function useNameTemplates(opts: { enabled: boolean }) {
  return useQuery({
    queryKey: ["cs-name-templates"],
    enabled: opts.enabled,
    queryFn: async () => {
      const { data } = await api.get<NameTemplate[]>(
        "/api/campaign-studio/name-templates",
      );
      return data;
    },
  });
}

export function useCreateNameTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: NameTemplateCreateRequest) => {
      const { data } = await api.post<NameTemplate>(
        "/api/campaign-studio/name-templates",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cs-name-templates"] });
    },
  });
}

export function useUpdateNameTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      template_id: number;
      patch: NameTemplateUpdateRequest;
    }) => {
      const { data } = await api.patch<NameTemplate>(
        `/api/campaign-studio/name-templates/${args.template_id}`,
        args.patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cs-name-templates"] });
    },
  });
}

export function useDeleteNameTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (template_id: number) => {
      await api.delete(
        `/api/campaign-studio/name-templates/${template_id}`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cs-name-templates"] });
    },
  });
}

// --- Campaign Studio: Flow 2 ----------------------------------------------

/** Add ad groups: each picked campaign's device (read from its existing
 *  ad groups) and whether the Devices step has to ask. */
export function useCampaignDevices(campaignIds: number[]) {
  const ids = [...campaignIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["cs-campaign-devices", ids],
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const qs = ids.map((id) => `campaign_ids=${id}`).join("&");
      const { data } = await api.get<CsCampaignDevicesResponse>(
        `/api/campaign-studio/campaign-devices?${qs}`,
      );
      return data;
    },
  });
}

export function useCreateAdGroupsFlow2() {
  return useMutation({
    mutationFn: async (payload: CsFlow2Request) => {
      const { data } = await api.post<CsJobCreateResponse>(
        "/api/campaign-studio/ad-groups",
        payload,
      );
      return data;
    },
  });
}

// --- Campaign Studio: Flow 3 (Bulk + CSV) ----------------------------------

export function useUploadBulkCsv() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<CsCsvUploadResponse>(
        "/api/campaign-studio/csv/upload",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },
  });
}

export function useBulkPreview() {
  return useMutation({
    mutationFn: async (payload: CsFlow3Request) => {
      const { data } = await api.post<CsFlow3PreviewResponse>(
        "/api/campaign-studio/bulk-campaigns/preview",
        payload,
      );
      return data;
    },
  });
}

export function useCreateBulkFlow3() {
  return useMutation({
    mutationFn: async (payload: CsFlow3Request) => {
      const { data } = await api.post<CsJobCreateResponse>(
        "/api/campaign-studio/bulk-campaigns",
        payload,
      );
      return data;
    },
  });
}

// --- Per-app Overview tile config (§36) ----------------------------------

export function useOverviewTilesConfig(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["overview-tiles", appId ?? null],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<OverviewTilesConfig>(
        "/api/app-settings/overview-tiles",
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 30_000,
  });
}

export function useOverviewEventSeries(
  appId: number | null | undefined,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: ["overview-event-series", appId ?? null, dateFrom, dateTo],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<OverviewEventSeriesResponse>(
        "/api/stats/overview-event-series",
        { params: { app_id: appId, date_from: dateFrom, date_to: dateTo } },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpsertOverviewTilesConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      body: OverviewTilesUpsertRequest;
    }) => {
      const { data } = await api.put<OverviewTilesConfig>(
        "/api/app-settings/overview-tiles",
        args.body,
        { params: { app_id: args.app_id } },
      );
      return data;
    },
    onSuccess: (_data, args) => {
      // Refresh the editor's own query first so the saved state lands
      // in the picker without a manual refetch.
      void qc.invalidateQueries({
        queryKey: ["overview-tiles", args.app_id],
      });
      // The Overview page reads `tile_config` from /api/stats/overview
      // — invalidating that family makes the next render swap to the
      // newly-saved tile mix immediately. The exact filter object
      // changes per page state, so we match the prefix instead of the
      // full key.
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

// --- ASA health (outage banner) -----------------------------------------

export type AsaHealth = {
  degraded: boolean;
  since: string | null;
  last_status: number | null;
};

/** Current Apple Search Ads health for the active org. Polled so the outage
 *  banner appears/clears within ~a minute. */
export function useAsaHealth() {
  return useQuery({
    queryKey: ["asa-health"],
    queryFn: async () => {
      const { data } = await api.get<AsaHealth>("/api/asa-health");
      return data;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

// ---- Autopilot: SKAG preset suggestions (§ Autopilot New / History) --------

/** Строка карточки Duplicate Keyword Guard — состав на момент прогона. */
export type DkgCardRow = {
  keyword_id: number;
  text: string;
  campaign_id: number;
  campaign_name: string | null;
  ad_group_id: number;
  ad_group_name: string | null;
  spend_7d: number;
  campaign_spend_7d: number;
  page: string | null;
  created_at: string | null;
  variant_of: string | null;
};

export type AutopilotSuggestion = TranslationFields & {
  id: number;
  app_id: number | null;
  rule_id: number | null;
  rule_name_snapshot: string | null;
  preset: string;
  search_term: string;
  country: string;
  status: "pending" | "applied" | "dismissed" | "snoozed" | "skipped";
  source_campaign_id: number | null;
  source_campaign_name: string | null;
  campaign_id: number | null;
  skip_reason: string | null;
  snooze_until: string | null;
  created_at: string;
  acted_at: string | null;
  acted_by_email: string | null;
  payload: {
    // SKAG suggestion
    campaign_name?: string;
    daily_budget?: number | string;
    bid_mode?: "manual" | "auto08";
    bid_value?: number | string | null;
    bid_estimate?: string | null;
    add_negative?: boolean;
    launch_immediately?: boolean;
    custom_product_page_id?: string | null;
    // Search Term Cleaner suggestion — metrics snapshot at suggestion time
    keyword?: string;
    action?: string;
    impressions?: number | null;
    installs?: number | null;
    ipm?: number | null;
    // Duplicate Keyword Guard — состав карточки (порядок §5, победитель
    // флагом, не позицией)
    rows?: DkgCardRow[];
    winner_keyword_id?: number;
    user_winner_keyword_id?: number | null;
    applied_winner_keyword_id?: number;
    swapped?: boolean;
    paused_keyword_ids?: number[];
  };
  /** Broad to SKAG Extractor (ТЗ BTSE §7): null — строки на карточке нет. */
  ecg_view?: { name: string | null; unavailable: boolean } | null;
  labels_view?: { id: number; name: string }[] | null;
};

/** Two-group source-campaign picker for the Search Term Cleaner
 *  configurator (ENABLED campaigns only, grouped Broad&SearchMatch /
 *  MaxConversions; campaigns in neither group are omitted). */
export type CleanerSourceCampaign = {
  campaign_id: number;
  name: string;
  status: string;
  group: "broad_search_match" | "max_conversions";
};

export function useCleanerSourceCampaigns(appId: number | null) {
  return useQuery({
    queryKey: ["cleaner-source-campaigns", appId],
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: CleanerSourceCampaign[] }>(
        "/api/autopilot/cleaner-source-campaigns",
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useSuggestions(
  tab: "new" | "history",
  page: { limit?: number; offset?: number } = {},
) {
  const limit = page.limit ?? 50;
  const offset = page.offset ?? 0;
  return useQuery({
    queryKey: ["autopilot-suggestions", tab, limit, offset],
    queryFn: async () => {
      const { data } = await api.get<{
        rows: AutopilotSuggestion[];
        total: number;
      }>("/api/autopilot/suggestions", { params: { tab, limit, offset } });
      return { rows: data.rows, total: data.total ?? data.rows.length };
    },
    staleTime: 20_000,
  });
}

export function useAutopilotBadge() {
  return useQuery({
    queryKey: ["autopilot-badge"],
    queryFn: async () => {
      const { data } = await api.get<{ count: number }>("/api/autopilot/badge");
      return data.count;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useMarkSuggestionsSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ seen: number; alerts_cleared: number }>(
        "/api/autopilot/suggestions/mark-seen",
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["autopilot-badge"] });
      // The bell's "new suggestion" alerts are dismissed by the same call.
      void qc.invalidateQueries({ queryKey: ["user-alerts"] });
    },
  });
}

function useSuggestionAction(verb: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(
        `/api/autopilot/suggestions/${id}/${verb}`,
      );
      return data as { ok: boolean; status?: string; skip_reason?: string; error?: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["autopilot-suggestions"] });
      qc.invalidateQueries({ queryKey: ["autopilot-badge"] });
      qc.invalidateQueries({ queryKey: ["campaigns-mirror"] });
    },
  });
}

export const useApplySuggestion = () => useSuggestionAction("apply");

/** Apply с выбранным победителем (Duplicate Keyword Guard: Swap / Choose
 *  winner). Отдельный хук, а не расширение `useSuggestionAction`: у того
 *  `variables` — число, и NewTab ищет по нему карточку для Undo. */
export function useApplySuggestionWithWinner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: number; winner_keyword_id: number | null }) => {
      const { data } = await api.post(
        `/api/autopilot/suggestions/${v.id}/apply`,
        { winner_keyword_id: v.winner_keyword_id },
      );
      return data as {
        ok: boolean;
        status?: string;
        skip_reason?: string;
        error?: string;
        paused?: number;
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["autopilot-suggestions"] });
      qc.invalidateQueries({ queryKey: ["autopilot-badge"] });
      qc.invalidateQueries({ queryKey: ["stats-keywords"] });
    },
  });
}
export const useSnoozeSuggestion = () => useSuggestionAction("snooze");
export const useUnsnoozeSuggestion = () => useSuggestionAction("unsnooze");
export const useDismissSuggestion = () => useSuggestionAction("dismiss");

export function useOrgBilling(orgId: number | null | undefined) {
  return useQuery({
    queryKey: ["org-billing", orgId],
    enabled: orgId != null,
    queryFn: async () => {
      const { data } = await api.get<BillingOverview>(
        `/api/orgs/${orgId}/billing`,
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/** Отмена подписки и отказ от неё.
 *
 *  Обе инвалидируют вкладку целиком: меняется не только состояние отмены —
 *  вместе с ним уезжают доступные действия, а по ним рисуются кнопки. */
export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: number) => {
      const { data } = await api.post<{
        subscription_id: string;
        effective_at: string | null;
      }>(`/api/orgs/${orgId}/subscription/cancel`);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-billing"] });
    },
  });
}

export function useResumeSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: number) => {
      const { data } = await api.post<{ subscription_id: string }>(
        `/api/orgs/${orgId}/subscription/resume`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-billing"] });
    },
  });
}

/** Свежая ссылка на PDF счёта.
 *
 *  Мутация, а не запрос: ссылка Paddle живёт около часа, и кэшировать её
 *  нельзя — у мутации кэша нет по определению. Ответ — JSON со ссылкой, а не
 *  редирект: токен лежит в localStorage и обычную навигацию не переживает. */
export function useInvoicePdf() {
  return useMutation({
    mutationFn: async (args: { orgId: number; transactionId: string }) => {
      const { data } = await api.post<{ url: string }>(
        `/api/orgs/${args.orgId}/invoices/${args.transactionId}/pdf`,
      );
      return data.url;
    },
  });
}

/** Спросить VIES про налоговый номер.
 *
 *  Отдельно от сохранения: у сохранения сети нет намеренно, а VIES лежит
 *  регулярно — привяжи мы одно к другому, клиент не смог бы записать свои
 *  же реквизиты, пока чужой сервис недоступен. */
export function useCheckVat() {
  return useMutation({
    mutationFn: async (args: {
      orgId: number;
      vatId?: string;
      country?: string;
    }) => {
      const { data } = await api.post<{
        status: "valid" | "invalid" | "unknown";
        reason: string | null;
        name: string | null;
        text: string;
      }>(`/api/orgs/${args.orgId}/legal/vat-check`, {
        vat_id: args.vatId ?? null,
        country: args.country ?? null,
      });
      return data;
    },
  });
}

/** Смена тарифа на живой подписке. На триале сервер откажет отдельным
 *  кодом — кнопки для этого случая в интерфейсе нет вовсе. */
export function useChangePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { orgId: number; plan: string }) => {
      const { data } = await api.post<{ subscription_id: string }>(
        `/api/orgs/${args.orgId}/subscription/plan`,
        { plan: args.plan },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-billing"] });
    },
  });
}

/** Покупка первой подписки: сервер заводит транзакцию, браузер идёт на
 *  страницу оплаты. Это не смена плана — менять нечего. */
export function useStartCheckout() {
  return useMutation({
    mutationFn: async (args: { orgId: number; plan: string }) => {
      const { data } = await api.post<{
        transaction_id: string;
        pay_url: string;
      }>(`/api/orgs/${args.orgId}/subscription/checkout`, { plan: args.plan });
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Встроенный график Ads Manager
// ---------------------------------------------------------------------------

type ChartLevel = "campaign" | "ad_group" | "keyword";

/** Дневные ряды по ОДНОЙ сущности.
 *
 *  ★ Запрос не уходит, пока не выделена ровно одна сущность: при нуле и
 *  при двух график по ТЗ показывает placeholder, и данные ему не нужны.
 *  `enabled` здесь дешевле, чем отменять уже начатый запрос. */
export function useEntityChart(
  appId: number | null | undefined,
  level: ChartLevel,
  entityId: number | null,
  dateFrom: string,
  dateTo: string,
  /** Кампания сущности. Не фильтр, а ВХОД В ИНДЕКС: ни один индекс
   *  `keyword_daily_stats` и `subscription_events` не начинается с
   *  `ad_group_id` или `keyword_id` — все ведут по `campaign_id`. Без него
   *  запрос сканирует весь период организации ради одной сущности. */
  campaignId?: number | null,
) {
  return useQuery({
    queryKey: [
      "entity-chart", appId ?? null, level, entityId, dateFrom, dateTo,
      campaignId ?? null,
    ],
    enabled: appId != null && entityId != null,
    queryFn: async () => {
      const { data } = await api.get<EntityChartResponse>(
        "/api/stats/entity-chart",
        {
          params: {
            app_id: appId, level, entity_id: entityId,
            date_from: dateFrom, date_to: dateTo,
            ...(campaignId != null ? { campaign_id: campaignId } : {}),
          },
        },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/** Маркеры изменений — дни, когда сущность меняли через OneAds. */
export function useEntityChanges(
  appId: number | null | undefined,
  level: ChartLevel,
  entityId: number | null,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: ["entity-changes", appId ?? null, level, entityId, dateFrom, dateTo],
    enabled: appId != null && entityId != null,
    queryFn: async () => {
      const { data } = await api.get<EntityChangesResponse>(
        "/api/stats/entity-changes",
        {
          params: {
            app_id: appId, level, entity_id: entityId,
            date_from: dateFrom, date_to: dateTo,
          },
        },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export type ChartPreset = { id: number; name: string; metrics: string[] };

export function useChartPresets(level: ChartLevel) {
  return useQuery({
    queryKey: ["chart-presets", level],
    queryFn: async () => {
      const { data } = await api.get<{
        presets: ChartPreset[];
        last_metrics: string[] | null;
      }>("/api/ads-manager/chart-presets", { params: { level } });
      return data;
    },
    staleTime: 60_000,
  });
}

export function useSaveChartPreset(level: ChartLevel) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { name: string; metrics: string[] }) => {
      const { data } = await api.post<ChartPreset>(
        "/api/ads-manager/chart-presets", { level, ...v });
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["chart-presets", level] }),
  });
}

export function useDeleteChartPreset(level: ChartLevel) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/ads-manager/chart-presets/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["chart-presets", level] }),
  });
}

/** Запомнить открытый набор метрик.
 *
 *  ★ Кэш пресетов НЕ сбрасывается: ответа у запроса нет, а сброс на
 *  каждой смене метрик перезапрашивал бы список на каждый клик. Значение
 *  нужно при следующем открытии, а не сейчас. */
export function useSetChartLastMetrics(level: ChartLevel) {
  return useMutation({
    mutationFn: async (metrics: string[]) => {
      await api.put("/api/ads-manager/chart-last-metrics", { level, metrics });
    },
  });
}

// --- Personal notification channels (TZ_ASO_Notifications_Delivery) ---------
// /api/telegram/link/* — the USER's own chat; /api/notification-channels — the
// e-mail half + the readiness summary the rule modal gates Save on (§5).

export type TelegramLinkStart = {
  deep_link: string;
  qr_svg: string;
  expires_at: string;
  bot_username: string;
  start_command: string;
};

export type TelegramLinkStatus = {
  state: "none" | "pending" | "linked" | "expired";
  chat_type: "private" | "group" | "supergroup" | null;
  chat_title: string | null;
  bot_username: string | null;
  expires_at: string | null;
  linked_at: string | null;
  verified_at: string | null;
  failed_at: string | null;
  last_error: string | null;
};

export type ChannelReadiness = {
  telegram: {
    ready: boolean;
    failed: boolean;
    linked: boolean;
    verified: boolean;
    last_error: string | null;
  };
  email: { ready: boolean; failed: boolean; last_error: string | null };
};

export type NotificationEmailStatus = {
  email: string;
  is_login_email: boolean;
  verified_at: string | null;
  pending_email: string | null;
  pending_expires_at: string | null;
  smtp_configured: boolean;
  failed_at: string | null;
  last_error: string | null;
};

const CHANNEL_KEYS = [
  ["telegram-link-status"],
  ["notification-channels"],
  ["notification-email"],
] as const;

function invalidateChannels(qc: ReturnType<typeof useQueryClient>) {
  for (const key of CHANNEL_KEYS) void qc.invalidateQueries({ queryKey: key });
}

/** Polls while a link is pending (the person is looking at the QR) — the
 *  worker reads Telegram every 3 s in that state, so 3 s here matches. */
export function useTelegramLinkStatus(opts?: { poll?: boolean }) {
  return useQuery({
    queryKey: ["telegram-link-status"],
    queryFn: async () => {
      const { data } = await api.get<TelegramLinkStatus>(
        "/api/telegram/link/status",
      );
      return data;
    },
    refetchInterval: (q) =>
      opts?.poll !== false && q.state.data?.state === "pending" ? 3000 : false,
    staleTime: 5_000,
  });
}

export function useTelegramLinkStart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<TelegramLinkStart>(
        "/api/telegram/link/start",
      );
      return data;
    },
    onSuccess: () => invalidateChannels(qc),
  });
}

export function useTelegramLinkTest() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ delivered: boolean; error: string | null }>(
        "/api/telegram/link/test",
      );
      return data;
    },
  });
}

export function useTelegramLinkConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<TelegramLinkStatus>(
        "/api/telegram/link/confirm",
      );
      return data;
    },
    onSuccess: () => invalidateChannels(qc),
  });
}

export function useTelegramUnlink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete("/api/telegram/link");
    },
    onSuccess: () => invalidateChannels(qc),
  });
}

export function useNotificationChannels() {
  return useQuery({
    queryKey: ["notification-channels"],
    queryFn: async () => {
      const { data } = await api.get<ChannelReadiness>(
        "/api/notification-channels",
      );
      return data;
    },
    staleTime: 10_000,
  });
}

export function useNotificationEmail() {
  return useQuery({
    queryKey: ["notification-email"],
    queryFn: async () => {
      const { data } = await api.get<NotificationEmailStatus>(
        "/api/notification-channels/email",
      );
      return data;
    },
    staleTime: 10_000,
  });
}

export function useNotificationEmailInit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => {
      const { data } = await api.post<{
        pending_email: string;
        expires_at: string;
        sent: boolean;
        smtp_configured: boolean;
      }>("/api/notification-channels/email/init", { email });
      return data;
    },
    onSuccess: () => invalidateChannels(qc),
  });
}

export function useNotificationEmailConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post<NotificationEmailStatus>(
        "/api/notification-channels/email/confirm",
        { code },
      );
      return data;
    },
    onSuccess: () => invalidateChannels(qc),
  });
}

export function useNotificationEmailTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        to: string;
        sent: boolean;
        smtp_configured: boolean;
      }>("/api/notification-channels/email/test");
      return data;
    },
    onSuccess: () => invalidateChannels(qc),
  });
}
